<template>
  <div id="app" class="home-page">
    <HeadNav
      :selected-language="currentLanguage"
      page="home"
      :gotodownload="gotodownload"
    />

    <main class="home-main">
      <section class="home-hero">
        <div class="hero-bg" aria-hidden="true">
          <span class="hero-bg-orb hero-bg-orb--one"></span>
          <span class="hero-bg-orb hero-bg-orb--two"></span>
          <span class="hero-bg-grid"></span>
        </div>
        <div class="hero-copy">
          <p class="security-marker">
            <i class="fa fa-shield-alt" aria-hidden="true"></i>
            {{ pageText.securityMarker }}
          </p>
          <h1>{{ homeCopy.heading }}</h1>
          <p class="hero-text">
            {{ homeCopy.heroText }}
          </p>
          <a class="primary-download" :href="site.downloadPath">
            <i class="fa fa-cloud-download-alt" aria-hidden="true"></i>
            {{ pageText.downloadButton }}
          </a>
          <p class="hero-note">
            {{ pageText.heroNote }}
          </p>
        </div>
        <div class="hero-visual" aria-hidden="true">
          <div class="hero-phone">
            <div class="hero-phone-screen">
              <img
                class="hero-phone-image"
                src="@/assets/Enhanced-Privacy-Options.webp"
                alt=""
              >
            </div>
          </div>
        </div>
      </section>

      <section class="content-section">
        <h2>{{ homeCopy.introTitle }}</h2>
        <h3 v-if="downloadStatusContent.title">{{ downloadStatusContent.title }}</h3>
        <p>
          {{ homeCopy.introText }}
        </p>
        <div v-if="downloadStatusContent.details && downloadStatusContent.details.length" class="download-status-details">
          <p v-for="detail in downloadStatusContent.details" :key="detail.label">
            <strong>{{ detail.label }}:</strong> {{ detail.value }}
          </p>
        </div>
        <p v-if="downloadStatusContent.securityCheck">
          <strong>{{ downloadStatusContent.securityCheck.label }}:</strong> {{ downloadStatusContent.securityCheck.value }}
        </p>
        <p v-if="downloadStatusContent.virusTotal" v-html="downloadStatusContent.virusTotal"></p>
        <div v-if="homeCopy.showQuickLinks !== false" class="quick-links">
          <a :href="site.apkUrl">{{ pageText.quickLinks.latest }}</a>
          <span>Android 5.0+</span>
          <a href="#download-guide">{{ pageText.quickLinks.install }}</a>
          <a href="#comparison">{{ pageText.quickLinks.comparison }}</a>
        </div>
      </section>

      <section v-if="userChoiceSectionItems.length" class="content-section">
        <h2>{{ pageText.userChoiceTitle }}</h2>
        <p v-html="pageText.userChoiceIntro"></p>
        <article v-for="section in userChoiceSectionItems" :key="section.heading" class="text-block">
          <h3>{{ section.heading }}</h3>
          <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
        </article>
      </section>

      <section v-else class="content-section feature-grid-section">
        <h2>{{ pageText.featureGridTitle }}</h2>
        <div class="feature-grid">
          <article v-for="feature in featureCards" :key="feature.title" class="feature-card">
            <div class="feature-card-icon" :class="`feature-card-icon--${feature.theme}`">
              <i class="fa" :class="feature.icon" aria-hidden="true"></i>
            </div>
            <div class="feature-card-body">
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.text }}</p>
            </div>
          </article>
        </div>
      </section>

      <section v-if="!userChoiceSectionItems.length" class="content-section">
        <p>
          <span v-html="pageText.brandIntro"></span>
        </p>
      </section>

      <section class="content-section">
        <h2>{{ pageText.appInfoTitle }}</h2>
        <div class="table-wrap">
          <table class="info-table">
            <tbody>
              <tr v-for="row in appInfoRows" :key="row.label">
                <th>{{ row.label }}</th>
                <td>{{ row.value }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="content-section">
        <h2>{{ pageText.featureSectionsTitle }}</h2>
        <article v-for="section in featureContentSections" :key="section.heading" class="text-block">
          <h3>{{ section.heading }}</h3>
          <p v-for="(paragraph, index) in section.paragraphs" :key="index" v-html="paragraph"></p>
          <ul v-if="section.items">
            <li v-for="item in section.items" :key="item">{{ item }}</li>
          </ul>
          <img
            v-if="section.image"
            class="text-image"
            :src="section.image"
            :alt="section.heading"
            loading="lazy"
            decoding="async"
          >
        </article>
      </section>

      <section class="content-section">
        <h2>{{ pageText.whatsNewTitle }}</h2>
        <p v-if="pageText.whatsNewIntro" v-html="pageText.whatsNewIntro"></p>
        <ul class="check-list">
          <li v-for="item in whatsNewItems" :key="item" v-html="item"></li>
        </ul>
      </section>

      <section id="download-guide" class="content-section">
        <h2>{{ pageText.androidTitle }}</h2>
        <p>
          {{ pageText.androidIntro }}
        </p>
        <ol class="step-timeline">
          <li v-for="(step, index) in androidStepItems" :key="step.title" class="step-timeline-item">
            <div class="step-timeline-marker" aria-hidden="true">{{ index + 1 }}</div>
            <div class="step-timeline-body">
              <strong class="step-timeline-title">{{ step.title }}</strong>
              <p class="step-timeline-text">{{ step.text }}</p>
            </div>
          </li>
        </ol>
      </section>

      <section v-if="notInstallingItems.length" class="content-section">
        <h2>{{ pageText.notInstallingTitle }}</h2>
        <p>
          {{ pageText.notInstallingIntro }}
        </p>
        <ul class="guide-dot-list">
          <li v-for="item in notInstallingItems" :key="item" v-html="item"></li>
        </ul>
      </section>

      <section class="content-section">
        <h2>{{ pageText.iosTitle }}</h2>
        <p>
          {{ pageText.iosIntroOne }}
        </p>
        <p>
          {{ pageText.iosIntroTwo }}
        </p>
        <h3>{{ pageText.iosSubtitle }}</h3>
        <p>
          {{ pageText.iosStepsIntro }}
        </p>
        <ul class="guide-dot-list">
          <li v-for="step in iosStepItems" :key="step">{{ step }}</li>
        </ul>
      </section>

      <section class="content-section">
        <h2>{{ pageText.pcTitle }}</h2>
        <p>
          {{ pageText.pcIntro }}
        </p>
        <ul class="guide-dot-list">
          <li v-for="step in pcStepItems" :key="step">{{ step }}</li>
        </ul>
      </section>

      <section class="content-section">
        <h2>{{ pageText.backupTitle }}</h2>
        <p>
          {{ pageText.backupIntro }}
        </p>
        <ul class="guide-dot-list">
          <li v-for="(step, index) in backupStepItems" :key="index" v-html="step"></li>
        </ul>
      </section>

      <section class="content-section">
        <h2>{{ pageText.updateTitle }}</h2>
        <p>
          {{ pageText.updateIntro }}
        </p>
        <ol class="step-timeline">
          <li v-for="(step, index) in updateStepItems" :key="index" class="step-timeline-item">
            <div class="step-timeline-marker" aria-hidden="true">{{ index + 1 }}</div>
            <div class="step-timeline-body">
              <p class="step-timeline-text step-timeline-text--solo">{{ step }}</p>
            </div>
          </li>
        </ol>
      </section>

      <section class="content-section">
        <h2>{{ pageText.safetyTitle }}</h2>
        <p>
          {{ pageText.safetyIntro }}
        </p>
        <p>{{ pageText.safetyLead }}</p>
        <ul class="guide-dot-list">
          <li v-for="tip in safetyTipItems" :key="tip">{{ tip }}</li>
        </ul>
        <p>
          {{ pageText.safetyOutro }}
        </p>
      </section>

      <section id="comparison" class="content-section">
        <h2>{{ pageText.comparisonTitle }}</h2>
        <div class="table-wrap">
          <table class="comparison-table">
            <thead>
              <tr>
                <th>{{ pageText.comparisonHeaders.feature }}</th>
                <th>{{ pageText.comparisonHeaders.gb }}</th>
                <th>{{ pageText.comparisonHeaders.official }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in comparisonTableRows" :key="row.label">
                <th>{{ row.label }}</th>
                <td>{{ row.gb }}</td>
                <td>{{ row.official }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="content-section pros-cons-section">
        <div class="pros-cons-grid">
          <div class="pros-cons-panel pros-cons-panel--pro">
            <h2 class="pros-cons-heading">
              <span class="pros-cons-heading-icon pros-cons-heading-icon--pro">
                <i class="fa fa-check" aria-hidden="true"></i>
              </span>
              {{ pageText.prosTitle }}
            </h2>
            <ul class="pros-cons-list">
              <li v-for="item in proItems" :key="item.text" class="pros-cons-item">
                <div class="pros-cons-icon pros-cons-icon--pro">
                  <i class="fa" :class="item.icon" aria-hidden="true"></i>
                </div>
                <p>{{ item.text }}</p>
              </li>
            </ul>
          </div>
          <div class="pros-cons-panel pros-cons-panel--con">
            <h2 class="pros-cons-heading">
              <span class="pros-cons-heading-icon pros-cons-heading-icon--con">
                <i class="fa fa-times" aria-hidden="true"></i>
              </span>
              {{ pageText.consTitle }}
            </h2>
            <ul class="pros-cons-list">
              <li v-for="item in conItems" :key="item.text" class="pros-cons-item">
                <div class="pros-cons-icon pros-cons-icon--con">
                  <i class="fa" :class="item.icon" aria-hidden="true"></i>
                </div>
                <p>{{ item.text }}</p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section class="content-section">
        <h2>{{ pageText.conclusionTitle }}</h2>
        <p>
          {{ pageText.conclusionOne }}
        </p>
        <p>
          {{ pageText.conclusionTwo }}
        </p>
      </section>

      <section class="content-section faq-section">
        <h2>{{ pageText.faqTitle }}</h2>
        <div class="faq-accordion">
          <div
            v-for="(faq, index) in faqItems"
            :key="faq.question"
            class="faq-item"
            :class="{ 'is-open': isFaqOpen(index) }"
          >
            <button
              type="button"
              class="faq-question"
              :aria-expanded="isFaqOpen(index) ? 'true' : 'false'"
              @click="toggleFaq(index)"
            >
              <span class="faq-question-text">{{ faq.question }}</span>
              <span class="faq-icon" aria-hidden="true"></span>
            </button>
            <div class="faq-answer">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';
import site from '@/config/site.json';

const PAGE_LINKS = {
  home: `${site.domain}/`,
  download: `${site.domain}${site.downloadPath}`,
};

const HOME_COPY = {
  en: {
    heading: 'GBWhatsApp APK Download Latest Version (2026) For Android',
    heroText: 'Download GB WhatsApp APK Latest Version For Android 2026.',
    introTitle: 'Download Status',
    introText:
      'The latest GB WhatsApp APK is currently available for Android. Before downloading, check the version number, file size, and Android requirements to make sure the APK is compatible with your device.',
  },
  id: {
    heading: 'WA GB Terbaru 2026 – Download WhatsApp GB',
    heroText: 'Download WA GB terbaru 2026 untuk Android dengan panduan instalasi dan informasi update WhatsApp GB.',
    introTitle: 'WA GB Terbaru 2026',
    introText:
      'Cari WA GB terbaru? Download versi WhatsApp GB terbaru untuk Android dan instal secara manual dalam beberapa menit. Halaman ini berisi file APK, langkah instalasi, info update, dan ringkasan fitur tambahan sebelum mengunduh.',
  },
  es: {
    heading: 'Descargar GB WhatsApp Actualizado 2026',
    heroText: 'Descargar GB WhatsApp actualizado 2026 para Android con guía de instalación e información de la última versión.',
    introTitle: 'GB WhatsApp Actualizado 2026',
    introText:
      '¿Buscas GB WhatsApp actualizado? Descarga la última versión para Android e instálala manualmente en pocos minutos. Esta página incluye el APK, los pasos de instalación, información de actualización y un resumen claro de sus funciones extra.',
  },
  br: {
    heading: 'Baixar WhatsApp GB Atualizado 2026',
    heroText: 'Baixe o WhatsApp GB atualizado 2026 para Android com guia de instalação e informações da última versão.',
    introTitle: 'WhatsApp GB Atualizado 2026',
    introText:
      'Procurando WhatsApp GB atualizado? Baixar a versão mais recente para Android e instalar manualmente leva apenas alguns minutos. Esta página inclui o APK, etapas de instalação, informações de atualização e um resumo dos recursos extras.',
  },
};

function pageLink(url, text, strong = false) {
  const label = strong ? `<strong>${text}</strong>` : text;
  return `<a href="${url}">${label}</a>`;
}

const PAGE_TEXT = {
  en: {
    securityMarker: 'Security Verified',
    downloadButton: 'APK Download',
    heroNote: 'The APK is available from a third-party source and is not distributed through Google Play.',
    quickLinks: {
      latest: 'Latest APK',
      install: 'Manual install guide',
      comparison: 'Feature comparison',
    },
    featureGridTitle: 'Why Users Download GB WhatsApp',
    userChoiceTitle: 'Why Users Choose GB WhatsApp',
    userChoiceIntro: `Many users choose ${pageLink(PAGE_LINKS.home, 'GB WhatsApp')} because they want more flexibility when using WhatsApp every day. Their main concerns are usually privacy, personalization, media sharing, and having more control over how chats are managed.`,
    brandIntro: `${pageLink(PAGE_LINKS.home, 'GB WhatsApp', true)} has become famous daily due to its extra customization options. You can download status quickly and use a dual WhatsApp account on a single phone. Easily download the most secure and bug-free latest version from our trusted site. It's only available on third-party sites due to some restrictions. Download the App without risk ban account issues. It's a perfect choice with a secure downloading process from our site.`,
    appInfoTitle: 'GBWhatsApp APK - App Info',
    featureSectionsTitle: 'Features of GB WhatsApp APK',
    whatsNewTitle: 'What’s New in GB WhatsApp APK?',
    whatsNewIntro: '<strong>GB WhatsApp V18.80</strong> is the latest version, focusing mainly on fixing common issues and improving overall stability and performance.',
    androidTitle: 'How to Download GB WhatsApp APK on Android?',
    androidIntro: 'GBWhatsApp APK is not available officially. You need a third-party source to download this application on your device. Here are some step-by-step instructions for downloading GB WhatsApp:',
    notInstallingTitle: 'Why Is GB WhatsApp Not Installing?',
    notInstallingIntro: 'GB WhatsApp may not install for several common reasons:',
    iosTitle: 'GB WhatsApp APK for iPhone',
    iosIntroOne: 'iOS provides a full range of rich functionalities. This app allows users to enjoy WhatsApp-like customization on iPads and iPhones. WhatsApp is available on the App Store, but its mod features are only available in GB WhatsApp Pro APK, so people often choose a third-party site to install and enjoy the customization experience.',
    iosIntroTwo: 'Select the IPA file for iOS devices and start your GB WhatsApp journey. You must install the IPA file on iOS by using AltStore and Cydia Impactor tools.',
    iosSubtitle: 'How to Download the GB WhatsApp APK on iOS?',
    iosStepsIntro: 'GB WhatsApp APK is officially unavailable on the App Store due to some issues. So, you need to download it using an IPA file from a trusted source. Follow these steps to get it on your iOS device:',
    pcTitle: 'How to Download GB WhatsApp APK on PC/Windows?',
    pcIntro: 'To download and install GB WhatsApp for PC, follow these steps:',
    backupTitle: 'How to Back up the GB WhatsApp APK app?',
    backupIntro: 'WhatsApp GB backup is not very hard for new users. It\'s a straightforward method used in the App to keep your data and avoid any risks. When you create a backup, your chats are safe for the long term uses. It\'s beneficial for safe data without restriction. Now, follow these steps to keep your chat backup:',
    updateTitle: 'How to Update GB WhatsApp App?',
    updateIntro: 'The GB WhatsApp update ensures your device is safe with the latest version and fixes bug issues. Developers have enhanced it with new features. Try to use up-to-date new versions of apps for engagement, created with customization features. It helps fix your app issues and add new terms. Let\'s find out how to update GB WhatsApp Pro easily:',
    safetyTitle: 'Is GB WhatsApp Safe?',
    safetyIntro: 'GB WhatsApp is a modified version of WhatsApp and is not distributed through Google Play. Because of this, users should understand the potential risks before installing it.',
    safetyLead: 'To reduce possible issues:',
    safetyOutro: 'No modified WhatsApp app can guarantee that an account will never face restrictions, so users should decide whether the additional features are worth the potential trade-offs.',
    comparisonTitle: 'Difference Between GB WhatsApp and Official WhatsApp',
    comparisonHeaders: {
      feature: 'Features',
      gb: 'GB WhatsApp APK',
      official: 'Official WhatsApp',
    },
    prosTitle: 'Pros',
    consTitle: 'Cons',
    conclusionTitle: 'Conclusion',
    conclusionOne: 'GB WhatsApp APK is one option for users who want a modified version of the official app with advanced features. You can customize your app to your liking. Users use built-in functions to enhance their privacy with social friends. It allows you to control your app fully. GB WhatsApp Pro creates a connection with an extra feature that hides your online visibility. Download the latest version of GB WhatsApp and get something extra from the app.',
    conclusionTwo: 'If you have any questions, feedback, or suggestions, feel free to contact us. Our team is always here to help with anything related to GB WhatsApp updates, features, or downloads.',
    faqTitle: 'FAQs',
  },
  id: {
    securityMarker: 'Terverifikasi Aman',
    downloadButton: 'Download APK',
    heroNote: 'APK tersedia dari sumber pihak ketiga dan tidak didistribusikan melalui Google Play.',
    quickLinks: {
      latest: 'APK terbaru',
      install: 'Panduan instal manual',
      comparison: 'Perbandingan fitur',
    },
    featureGridTitle: 'Alasan Pengguna Download WA GB',
    brandIntro: `${pageLink(PAGE_LINKS.home, 'WA GB', true)} semakin banyak dicari karena pilihan kustomisasinya lebih lengkap. Pengguna bisa menyimpan status dengan cepat dan memakai dua akun WhatsApp di satu HP. File APK hanya tersedia lewat situs pihak ketiga karena tidak ada di toko aplikasi resmi, jadi pastikan membaca panduan sebelum download.`,
    appInfoTitle: 'WA GB APK - Info Aplikasi',
    featureSectionsTitle: 'Fitur WA GB APK',
    whatsNewTitle: 'Apa yang Baru di WhatsApp GB APK?',
    androidTitle: 'Cara Download WA GB APK di Android',
    androidIntro: 'WA GB APK tidak tersedia secara resmi di Google Play. Untuk memasangnya, pengguna perlu mengunduh file dari sumber pihak ketiga yang dipercaya. Ikuti langkah berikut sebelum instalasi:',
    iosTitle: 'WhatsApp GB APK untuk iPhone',
    iosIntroOne: 'Di perangkat iOS, fitur mod seperti kustomisasi tampilan tidak tersedia melalui aplikasi resmi di App Store. Karena itu, sebagian pengguna mencari file IPA dari sumber pihak ketiga untuk mencoba pengalaman serupa di iPhone atau iPad.',
    iosIntroTwo: 'Pilih file IPA yang sesuai untuk iOS. Proses instalasi biasanya membutuhkan alat seperti AltStore atau Cydia Impactor.',
    iosSubtitle: 'Cara Download WA GB APK di iOS',
    iosStepsIntro: 'WhatsApp GB APK tidak tersedia di App Store. Jika ingin mencoba versi iOS, gunakan file IPA dari sumber yang dipercaya dan ikuti langkah berikut:',
    pcTitle: 'Cara Download WA GB APK di PC/Windows',
    pcIntro: 'Untuk memasang WA GB di PC atau Windows, ikuti langkah berikut:',
    backupTitle: 'Cara Backup Aplikasi WA GB APK',
    backupIntro: 'Backup WhatsApp GB membantu menjaga chat tetap aman saat update, pindah versi, atau ganti perangkat. Prosesnya sederhana dan bisa dilakukan dari menu pengaturan aplikasi:',
    updateTitle: 'Cara Update Aplikasi WA GB',
    updateIntro: 'Update WA GB membantu mendapatkan versi terbaru, perbaikan bug, dan peningkatan kompatibilitas. Jika aplikasi tidak menampilkan update otomatis, pengguna bisa mengecek versi baru melalui situs ini:',
    safetyTitle: 'Apakah WA GB Aman?',
    safetyIntro: 'WA GB adalah versi modifikasi dari WhatsApp dan tidak didistribusikan melalui Google Play. Karena itu, pengguna perlu memahami potensi risiko sebelum memasang APK.',
    safetyLead: 'Untuk mengurangi risiko, perhatikan hal berikut:',
    safetyOutro: 'Tidak ada aplikasi WhatsApp mod yang bisa menjamin akun selalu bebas pembatasan. Gunakan fitur tambahan dengan memahami konsekuensinya.',
    comparisonTitle: 'Perbedaan WA GB dan WhatsApp Resmi',
    comparisonHeaders: {
      feature: 'Fitur',
      gb: 'WA GB APK',
      official: 'WhatsApp Resmi',
    },
    prosTitle: 'Kelebihan',
    consTitle: 'Kekurangan',
    conclusionTitle: 'Kesimpulan',
    conclusionOne: 'WA GB APK bisa menjadi pilihan bagi pengguna yang membutuhkan fitur tambahan di luar aplikasi resmi. Pengguna dapat menyesuaikan tampilan, mengatur privasi, menyimpan status, dan memakai fitur lain sesuai versi yang digunakan.',
    conclusionTwo: 'Jika ada pertanyaan, masukan, atau saran, silakan hubungi kami. Tim kami membantu informasi terkait update WhatsApp GB, fitur, dan download APK.',
    faqTitle: 'FAQ',
  },
  es: {
    securityMarker: 'Seguridad verificada',
    downloadButton: 'Descargar APK',
    heroNote: 'El APK está disponible desde una fuente de terceros y no se distribuye a través de Google Play.',
    quickLinks: {
      latest: 'APK más reciente',
      install: 'Guía de instalación manual',
      comparison: 'Comparación de funciones',
    },
    featureGridTitle: 'Por qué los usuarios descargan GB WhatsApp',
    brandIntro: `${pageLink(PAGE_LINKS.home, 'GB WhatsApp', true)} se ha vuelto popular por sus opciones adicionales de personalización. Puedes descargar estados rápidamente y usar dos cuentas de WhatsApp en un solo teléfono. Descarga fácilmente la versión más reciente, segura y sin errores desde nuestro sitio de confianza. Solo está disponible en sitios de terceros debido a algunas restricciones. Descarga la aplicación sin problemas de riesgo de bloqueo de cuenta. Es una opción ideal con un proceso de descarga seguro desde nuestro sitio.`,
    appInfoTitle: 'GBWhatsApp APK - Información de la aplicación',
    featureSectionsTitle: 'Funciones de GB WhatsApp APK',
    whatsNewTitle: '¿Qué hay de nuevo en GB WhatsApp APK?',
    androidTitle: '¿Cómo descargar GB WhatsApp APK en Android?',
    androidIntro: 'GBWhatsApp APK no está disponible oficialmente. Necesitas una fuente de terceros para descargar esta aplicación en tu dispositivo. Aquí tienes instrucciones paso a paso para descargar GB WhatsApp:',
    iosTitle: 'GB WhatsApp APK para iPhone',
    iosIntroOne: 'iOS ofrece una amplia variedad de funciones. Esta aplicación permite a los usuarios disfrutar de una personalización similar a WhatsApp en iPads y iPhones. WhatsApp está disponible en la App Store, pero sus funciones mod solo están disponibles en GB WhatsApp Pro APK, por eso muchas personas eligen un sitio de terceros para instalarla y disfrutar de la experiencia de personalización.',
    iosIntroTwo: 'Selecciona el archivo IPA para dispositivos iOS y empieza tu experiencia con GB WhatsApp. Debes instalar el archivo IPA en iOS usando herramientas como AltStore y Cydia Impactor.',
    iosSubtitle: '¿Cómo descargar GB WhatsApp APK en iOS?',
    iosStepsIntro: 'GB WhatsApp APK no está disponible oficialmente en la App Store debido a algunos problemas. Por eso, debes descargarlo usando un archivo IPA desde una fuente confiable. Sigue estos pasos para tenerlo en tu dispositivo iOS:',
    pcTitle: '¿Cómo descargar GB WhatsApp APK en PC/Windows?',
    pcIntro: 'Para descargar e instalar GB WhatsApp para PC, sigue estos pasos:',
    backupTitle: '¿Cómo hacer una copia de seguridad de la aplicación GB WhatsApp APK?',
    backupIntro: 'La copia de seguridad de WhatsApp GB no es difícil para los usuarios nuevos. Es un método sencillo dentro de la aplicación para conservar tus datos y evitar riesgos. Cuando creas una copia de seguridad, tus chats quedan protegidos a largo plazo. Es útil para mantener los datos seguros sin restricciones. Ahora sigue estos pasos para guardar la copia de seguridad de tus chats:',
    updateTitle: '¿Cómo actualizar la aplicación GB WhatsApp?',
    updateIntro: 'La actualización de GB WhatsApp ayuda a mantener tu dispositivo seguro con la última versión y corrige errores. Los desarrolladores la han mejorado con nuevas funciones. Intenta usar versiones actualizadas de las aplicaciones para aprovechar sus funciones de personalización. También ayuda a solucionar problemas de la app y añadir novedades. Veamos cómo actualizar GB WhatsApp Pro fácilmente:',
    safetyTitle: '¿Es seguro GB WhatsApp?',
    safetyIntro: 'GB WhatsApp es una versión modificada de WhatsApp y no se distribuye a través de Google Play. Por eso, los usuarios deben entender los posibles riesgos antes de instalarla.',
    safetyLead: 'Para reducir posibles problemas:',
    safetyOutro: 'Ninguna aplicación modificada de WhatsApp puede garantizar que una cuenta nunca enfrentará restricciones, así que los usuarios deben decidir si las funciones adicionales compensan los posibles riesgos.',
    comparisonTitle: 'Diferencia entre GB WhatsApp y WhatsApp oficial',
    comparisonHeaders: {
      feature: 'Funciones',
      gb: 'GB WhatsApp APK',
      official: 'WhatsApp oficial',
    },
    prosTitle: 'Ventajas',
    consTitle: 'Desventajas',
    conclusionTitle: 'Conclusión',
    conclusionOne: 'GB WhatsApp APK es una opción para usuarios que quieren una versión modificada de la aplicación oficial con funciones avanzadas. Puedes personalizar la app a tu gusto. Los usuarios usan funciones integradas para mejorar su privacidad con amigos y contactos. Te permite controlar la aplicación por completo. GB WhatsApp Pro añade una función extra que oculta tu visibilidad en línea. Descarga la última versión de GB WhatsApp y consigue algo más de la aplicación.',
    conclusionTwo: 'Si tienes preguntas, comentarios o sugerencias, no dudes en contactarnos. Nuestro equipo siempre está disponible para ayudarte con cualquier tema relacionado con actualizaciones, funciones o descargas de GB WhatsApp.',
    faqTitle: 'Preguntas frecuentes',
  },
  br: {
    securityMarker: 'Segurança verificada',
    downloadButton: 'Baixar APK',
    heroNote: 'O APK está disponível por fonte de terceiros e não é distribuído pelo Google Play.',
    quickLinks: {
      latest: 'APK mais recente',
      install: 'Guia de instalação manual',
      comparison: 'Comparação de recursos',
    },
    featureGridTitle: 'Por que os usuários querem baixar WhatsApp GB',
    brandIntro: `${pageLink(PAGE_LINKS.home, 'WhatsApp GB', true)} ganhou popularidade por oferecer mais opções de personalização. É possível salvar status rapidamente e usar uma segunda conta do WhatsApp no mesmo celular. Como o APK não está em lojas oficiais, leia as instruções antes de baixar.`,
    appInfoTitle: 'WhatsApp GB APK - Informações do aplicativo',
    featureSectionsTitle: 'Recursos do WhatsApp GB APK',
    whatsNewTitle: 'O que há de novo no WhatsApp GB APK?',
    androidTitle: 'Como baixar WhatsApp GB APK no Android',
    androidIntro: 'O WhatsApp GB APK não está disponível oficialmente no Google Play. Para instalar, é preciso baixar o arquivo de uma fonte de terceiros confiável. Veja o passo a passo:',
    iosTitle: 'WhatsApp GB APK para iPhone',
    iosIntroOne: 'No iOS, recursos de modificação e personalização não ficam disponíveis pelo aplicativo oficial da App Store. Por isso, alguns usuários procuram arquivos IPA em fontes de terceiros para testar uma experiência parecida no iPhone ou iPad.',
    iosIntroTwo: 'Escolha um arquivo IPA compatível com iOS. A instalação costuma exigir ferramentas como AltStore ou Cydia Impactor.',
    iosSubtitle: 'Como baixar WhatsApp GB APK no iOS',
    iosStepsIntro: 'O WhatsApp GB APK não está disponível na App Store. Se quiser testar a versão para iOS, use um arquivo IPA de fonte confiável e siga estes passos:',
    pcTitle: 'Como baixar WhatsApp GB APK no PC/Windows',
    pcIntro: 'Para instalar o WhatsApp GB no PC ou Windows, siga estes passos:',
    backupTitle: 'Como fazer backup do WhatsApp GB APK',
    backupIntro: 'O backup do WhatsApp GB ajuda a proteger suas conversas ao atualizar, trocar de versão ou mudar de aparelho. O processo é simples e pode ser feito nas configurações do aplicativo:',
    updateTitle: 'Como atualizar o WhatsApp GB',
    updateIntro: 'Atualizar o WhatsApp GB ajuda a usar a versão mais recente, com correções de bugs e melhorias de compatibilidade. Se o app não mostrar atualização automática, confira a nova versão pelo site:',
    safetyTitle: 'O WhatsApp GB é seguro?',
    safetyIntro: 'O WhatsApp GB é uma versão modificada do WhatsApp e não é distribuído pelo Google Play. Por isso, o usuário deve entender os possíveis riscos antes de instalar o APK.',
    safetyLead: 'Para reduzir problemas, observe estes pontos:',
    safetyOutro: 'Nenhum mod do WhatsApp garante que a conta nunca sofrerá restrições. Use os recursos extras entendendo os possíveis impactos.',
    comparisonTitle: 'Diferenças entre WhatsApp GB e WhatsApp oficial',
    comparisonHeaders: {
      feature: 'Recursos',
      gb: 'WhatsApp GB APK',
      official: 'WhatsApp oficial',
    },
    prosTitle: 'Vantagens',
    consTitle: 'Desvantagens',
    conclusionTitle: 'Conclusão',
    conclusionOne: 'O WhatsApp GB APK pode ser uma opção para quem procura recursos extras além do aplicativo oficial. O usuário consegue ajustar a aparência, controlar mais opções de privacidade, salvar status e usar outras funções conforme a versão instalada.',
    conclusionTwo: 'Se tiver dúvidas, sugestões ou comentários, entre em contato. Nossa equipe ajuda com informações sobre atualização do WhatsApp GB, recursos e como baixar o APK.',
    faqTitle: 'Perguntas frequentes',
  },
};

const ID_CONTENT = {
  features: [
    {
      title: 'Sembunyikan status online',
      text: 'Tetap aktif tanpa menampilkan bahwa kamu sedang online.',
      icon: 'fa-shield-alt',
      theme: 'green',
    },
    {
      title: 'Download status',
      text: 'Simpan foto dan video dari Status WhatsApp langsung ke HP.',
      icon: 'fa-download',
      theme: 'blue',
    },
    {
      title: 'Kirim media lebih besar',
      text: 'Bagikan foto, video, dan dokumen berukuran lebih besar dengan lebih mudah.',
      icon: 'fa-file-image',
      theme: 'orange',
    },
    {
      title: 'Balasan otomatis',
      text: 'Kirim respons otomatis saat kamu sibuk atau jauh dari HP.',
      icon: 'fa-reply',
      theme: 'teal',
    },
    {
      title: 'Tema dan kustomisasi',
      text: 'Ubah warna, ikon, font, dan tampilan chat lebih bebas dari aplikasi resmi.',
      icon: 'fa-palette',
      theme: 'purple',
    },
    {
      title: 'Dukungan dua akun',
      text: 'Gunakan akun WhatsApp kedua di perangkat Android yang sama.',
      icon: 'fa-clone',
      theme: 'indigo',
    },
  ],
  appInfo: [
    { label: 'Nama Aplikasi', value: 'WA GB' },
    { label: 'Nama Paket', value: 'com.gbwhatsapp' },
    { label: 'Versi', value: 'Terbaru' },
    { label: 'Ukuran File', value: '90 MB' },
    { label: 'Minimal Android', value: '5.0+' },
    { label: 'Developer / Mod', value: 'Tidak resmi' },
    { label: 'Kategori', value: 'Komunikasi' },
    { label: 'Lisensi', value: 'Gratis (mod tidak resmi)' },
    { label: 'Terakhir Diperbarui', value: '2 hari lalu' },
    { label: 'Jenis File', value: 'APK' },
    {
      label: 'Izin',
      value: 'Kontak, Penyimpanan, Kamera, Mikrofon, Jaringan, Notifikasi',
    },
    { label: 'Dukungan Multi-Akun', value: 'Ya' },
    { label: 'Aplikasi Dasar', value: 'WhatsApp Messenger (com.whatsapp)' },
    {
      label: 'Catatan',
      value: 'Mod tidak resmi; tidak berafiliasi dengan WhatsApp. Gunakan dengan risiko sendiri.',
    },
  ],
  featureSections: [
    {
      heading: 'Opsi Privasi Lebih Lengkap',
      paragraphs: [
        `${pageLink(PAGE_LINKS.download, 'WA GB Pro', true)} memberi kontrol privasi tambahan, seperti menyembunyikan centang biru, menonaktifkan indikator mengetik, mematikan centang kedua, dan memutar voice note tanpa tanda baca. Pengaturan ini membantu menyesuaikan privasi sesuai kebutuhan.`,
      ],
      image: require('@/assets/hide-blueticks.webp'),
    },
    {
      heading: 'Mode DND',
      paragraphs: [
        'Mode DND membantu saat kamu tidak ingin terganggu. Aktifkan mode ini untuk menghentikan pesan dan notifikasi dari aplikasi tanpa harus menyalakan mode pesawat.',
      ],
      image: require('@/assets/DND.webp'),
    },
    {
      heading: 'Pesan Siaran',
      paragraphs: [
        'Perlu mengirim pesan yang sama ke banyak kontak? Fitur siaran memungkinkan satu pesan dikirim ke beberapa orang sekaligus tanpa membuat grup. Beberapa versi juga menyediakan penjadwalan pesan.',
      ],
    },
    {
      heading: 'Download Status',
      paragraphs: [
        'Simpan foto dan video dari status teman langsung ke galeri tanpa aplikasi downloader tambahan. Setelah diunduh, file bisa dibuka kembali kapan saja.',
      ],
    },
    {
      heading: 'Berbagi Media Lebih Besar',
      paragraphs: [
        'WA GB menaikkan batas berbagi file dibanding aplikasi resmi. Tergantung versi, pengguna bisa mengirim video, audio, dokumen, dan lebih banyak foto dalam satu pesan dengan kualitas gambar yang lebih baik.',
      ],
    },
    {
      heading: 'Filter Pesan',
      paragraphs: [
        'Temukan chat penting lebih cepat dengan filter pesan. Fitur ini membantu mencari pesan belum dibaca, kontak tertentu, atau percakapan di daftar chat yang ramai.',
      ],
      image: require('@/assets/message-filter.png'),
    },
    {
      heading: 'Tema dan Emoji',
      paragraphs: [
        'Personalisasi aplikasi dengan tema, font, wallpaper, dan pilihan emoji tambahan. Tampilan antarmuka bisa diubah tanpa memengaruhi chat atau media.',
      ],
      image: require('@/assets/themes-4k-gbwhatsdroid.webp'),
    },
    {
      heading: 'Pengaturan Privasi WhatsApp GB',
      paragraphs: [
        'Banyak pengguna memilih WhatsApp GB karena pengaturan privasinya lebih fleksibel. Pada beberapa versi, pengguna dapat menyembunyikan status online, membekukan terakhir dilihat, menonaktifkan indikator mengetik, atau mengatur aktivitas tertentu.',
      ],
    },
    {
      heading: 'Kunci Aplikasi',
      paragraphs: [
        'Lindungi chat dengan kunci bawaan. Jika perangkat mendukung, aplikasi dapat dibuka memakai PIN, sandi, atau sidik jari tanpa aplikasi pengunci tambahan.',
      ],
    },
    {
      heading: 'Dukungan Banyak Bahasa',
      paragraphs: [
        'Aplikasi mendukung beberapa bahasa sehingga lebih mudah digunakan di berbagai wilayah. Pilih bahasa yang tersedia melalui menu pengaturan pada versi yang kamu pakai.',
      ],
    },
    {
      heading: 'Bekukan Terakhir Dilihat',
      paragraphs: [
        'Beberapa versi memungkinkan pengguna menahan status terakhir dilihat pada waktu tertentu. Dengan begitu, aktivitas terbaru tidak langsung terlihat oleh orang lain.',
      ],
    },
    {
      heading: 'Berbagi Lokasi Langsung',
      paragraphs: [
        'Bagikan lokasi real-time kepada teman atau keluarga saat chat. Fitur ini berguna saat bertemu seseorang atau memberi tahu posisi saat bepergian.',
      ],
    },
    {
      heading: 'Mode Gelap dan Mode Terang',
      paragraphs: [
        'Ganti mode terang atau gelap sesuai preferensi dan pengaturan sistem HP. Tema khusus juga dapat dipakai untuk tampilan yang lebih personal.',
      ],
    },
    {
      heading: 'Teruskan Pesan Tanpa Label',
      paragraphs: [
        'Pada beberapa versi, pesan dapat diteruskan tanpa menampilkan label "Diteruskan". Ketersediaan fitur dapat berbeda tergantung rilis aplikasi.',
      ],
      image: require('@/assets/disable-forwarded.png'),
    },
    {
      heading: 'Update Rutin',
      paragraphs: [
        'Versi baru WA GB biasanya membawa perbaikan bug, peningkatan kompatibilitas, dan pembaruan fitur. Memakai versi terbaru dapat membantu mengurangi masalah pada rilis lama.',
      ],
    },
  ],
  whatsNew: [
    'Lebih banyak opsi untuk mengelola konten yang sudah diunduh.',
    'Dukungan banyak bahasa dan fitur terjemahan chat.',
    'Opsi dukungan pelanggan di situs untuk membantu kendala pengguna.',
    'Kontrol privasi seperti sembunyikan status dilihat dan bekukan terakhir dilihat.',
    'Proses restore backup untuk pindah perangkat.',
    'Akses ke toko emoji dan toko wallpaper.',
    'Pengelolaan penyimpanan dan emoji unduhan yang lebih baik.',
  ],
  androidSteps: [
    {
      title: 'Langkah 1: Aktifkan Sumber Tidak Dikenal',
      text: 'Sebelum memasang file APK, aktifkan izin Sumber Tidak Dikenal di pengaturan perangkat.',
    },
    {
      title: 'Langkah 2: Buka Sumber Tepercaya',
      text: 'Cari download WA GB dari situs yang dipercaya karena aplikasi ini tidak tersedia resmi di Google Play.',
    },
    {
      title: 'Langkah 3: Download dan Instal',
      text: 'Buka halaman download, tekan tombol unduh, lalu cari file di folder unduhan. Ketuk file APK untuk memulai instalasi.',
    },
    {
      title: 'Langkah 4: Buka dan Verifikasi',
      text: 'Setelah terpasang, buka aplikasi dan verifikasi nomor telepon dengan kode OTP 6 digit.',
    },
  ],
  iosSteps: [
    'Buka Safari atau browser lain, lalu cari file IPA.',
    'Pilih situs pihak ketiga yang dipercaya dan cari tombol download.',
    'Tekan tombol Download untuk menyimpan file ke perangkat.',
    'Buka file dan mulai proses instalasi.',
    'Dalam beberapa menit, ikon aplikasi akan muncul di layar perangkat.',
    'Buka aplikasi dan verifikasi dengan nomor telepon.',
  ],
  pcSteps: [
    'Download dan instal emulator Android di PC.',
    'Download file WA GB Pro dari sumber tepercaya dan buka melalui emulator.',
    'Gunakan LD Player, NOx, atau BlueStacks untuk proses instalasi di PC.',
    'Tarik file APK ke jendela emulator untuk menyelesaikan instalasi.',
    'Setelah aplikasi terpasang, verifikasi nomor dengan kode OTP 6 digit.',
    'Buka aplikasi dan gunakan fitur privasi serta kustomisasi yang tersedia.',
    'Kamu juga bisa memindai kode QR WhatsApp Web untuk memakai versi web.',
  ],
  backupSteps: [
    'Buka WA GB, lalu masuk ke pengaturan lewat ikon tiga titik di kanan atas.',
    'Cari menu <strong>"GB Settings"</strong>, lalu buka sub-pengaturan aplikasi.',
    'Pilih opsi Backup, masuk ke <strong>"Chat Backup"</strong>, lalu ketuk menu tersebut.',
    'Atur backup otomatis sesuai kebutuhan, misalnya harian, mingguan, atau bulanan.',
    'Aktifkan auto-backup agar chat lebih mudah dipulihkan saat akun bermasalah atau perangkat diganti.',
  ],
  updateSteps: [
    'Buka pengaturan WA GB APK di aplikasi',
    'Cari tombol Updates, lalu ketuk opsi Check for updates',
    'Jika update tersedia, tekan tombol Download untuk mengambil versi terbaru',
    'Instal versi baru di atas versi lama yang sudah ada di perangkat',
    'Jika update tidak muncul, buka situs ini untuk mendapatkan versi aplikasi terbaru',
  ],
  safetyTips: [
    'Download APK dari sumber yang kamu percaya.',
    'Gunakan versi terbaru, bukan rilis lama yang sudah tertinggal.',
    'Backup chat sebelum update atau berpindah versi.',
    'Biarkan Google Play Protect tetap aktif jika memungkinkan.',
    'Hindari APK yang dimodifikasi oleh sumber tidak dikenal.',
  ],
  comparisonRows: [
    {
      label: 'Kustomisasi',
      gb: 'Tema, wallpaper, dan font yang bisa disesuaikan',
      official: 'Pilihan terang dan gelap lebih terbatas',
    },
    {
      label: 'Opsi Privasi',
      gb: 'Bekukan online, sembunyikan centang biru, sembunyikan indikator mengetik',
      official: 'Privasi dasar',
    },
    {
      label: 'Dua Akun',
      gb: 'Ya',
      official: 'Umumnya satu akun',
    },
    {
      label: 'Berbagi Media',
      gb: 'Mendukung hingga 200 gambar',
      official: 'Mengirim sekitar 30 gambar sekaligus',
    },
    {
      label: 'Panjang Status',
      gb: '255 karakter',
      official: '139 karakter',
    },
    {
      label: 'Kunci Aplikasi',
      gb: 'Bawaan',
      official: 'Memakai pengaturan atau aplikasi eksternal',
    },
    {
      label: 'Update',
      gb: 'Update manual',
      official: 'Update otomatis',
    },
    {
      label: 'Keamanan',
      gb: 'Ada risiko pembatasan akun',
      official: 'Lebih aman karena resmi',
    },
  ],
  pros: [
    {
      icon: 'fa-cogs',
      text: 'Banyak opsi kustomisasi tersedia',
    },
    {
      icon: 'fa-palette',
      text: 'Ubah wallpaper, tema, dan gaya font',
    },
    {
      icon: 'fa-lock',
      text: 'Kunci aplikasi untuk menjaga data pribadi',
    },
    {
      icon: 'fa-history',
      text: 'Bekukan terakhir dilihat pada waktu tertentu',
    },
    {
      icon: 'fa-check-double',
      text: 'Update membantu memperbaiki bug dan masalah',
    },
  ],
  cons: [
    {
      icon: 'fa-ban',
      text: 'Ada risiko pembatasan sementara pada akun',
    },
    {
      icon: 'fa-exclamation-triangle',
      text: 'Tidak sesuai dengan kebijakan resmi WhatsApp',
    },
    {
      icon: 'fa-times-circle',
      text: 'Tidak tersedia di Google Play Store',
    },
  ],
  faqs: [
    {
      question: 'Apakah WA GB bisa dipakai bersama WhatsApp resmi di satu perangkat?',
      answer:
        'Ya, WA GB mendukung dua akun. Pengguna dapat menjalankan WA GB dan WhatsApp resmi di perangkat yang sama.',
    },
    {
      question: 'Apakah WhatsApp GB mendukung panggilan terenkripsi end-to-end?',
      answer:
        'WhatsApp GB memakai layanan WhatsApp, tetapi fitur modifikasi dapat memengaruhi keamanan. Aplikasi resmi tetap menjadi pilihan terbaik untuk perlindungan penuh.',
    },
    {
      question: 'Bisakah chat dari WhatsApp resmi dipindahkan ke WA GB?',
      answer:
        'Ya, pengguna bisa membuat backup chat dari WhatsApp resmi lalu memulihkannya saat memasang APK. Pastikan backup selesai agar chat dan media tidak hilang.',
    },
  ],
};

const BR_CONTENT = {
  features: [
    {
      title: 'Ocultar status online',
      text: 'Continue ativo sem mostrar quando você está online.',
      icon: 'fa-shield-alt',
      theme: 'green',
    },
    {
      title: 'Baixar status',
      text: 'Salve fotos e vídeos do Status do WhatsApp direto no celular.',
      icon: 'fa-download',
      theme: 'blue',
    },
    {
      title: 'Enviar mídias maiores',
      text: 'Compartilhe fotos, vídeos e documentos maiores com mais facilidade.',
      icon: 'fa-file-image',
      theme: 'orange',
    },
    {
      title: 'Resposta automática',
      text: 'Envie respostas automáticas quando estiver ocupado ou longe do celular.',
      icon: 'fa-reply',
      theme: 'teal',
    },
    {
      title: 'Temas e personalização',
      text: 'Altere cores, ícones, fontes e aparência das conversas além das opções oficiais.',
      icon: 'fa-palette',
      theme: 'purple',
    },
    {
      title: 'Suporte a duas contas',
      text: 'Use uma segunda conta do WhatsApp no mesmo dispositivo Android.',
      icon: 'fa-clone',
      theme: 'indigo',
    },
  ],
  appInfo: [
    { label: 'Nome do aplicativo', value: 'WhatsApp GB' },
    { label: 'Nome do pacote', value: 'com.gbwhatsapp' },
    { label: 'Versão', value: 'Mais recente' },
    { label: 'Tamanho do arquivo', value: '90 MB' },
    { label: 'Requer Android', value: '5.0+' },
    { label: 'Desenvolvedor / Mod', value: 'Não oficial' },
    { label: 'Categoria', value: 'Comunicação' },
    { label: 'Licença', value: 'Grátis (mod não oficial)' },
    { label: 'Última atualização', value: 'Há 2 dias' },
    { label: 'Tipo de arquivo', value: 'APK' },
    {
      label: 'Permissões',
      value: 'Contatos, Armazenamento, Câmera, Microfone, Rede, Notificações',
    },
    { label: 'Suporte a múltiplas contas', value: 'Sim' },
    { label: 'Aplicativo base', value: 'WhatsApp Messenger (com.whatsapp)' },
    {
      label: 'Observações',
      value: 'Mod não oficial; não afiliado ao WhatsApp. Use por sua conta e risco.',
    },
  ],
  featureSections: [
    {
      heading: 'Opções de privacidade avançadas',
      paragraphs: [
        `${pageLink(PAGE_LINKS.download, 'Baixar WhatsApp GB Pro', true)} permite ajustar mais opções de privacidade, como ocultar o tique azul, desativar o indicador de digitação, esconder o segundo tique e ouvir áudios sem confirmação de leitura.`,
      ],
      image: require('@/assets/hide-blueticks.webp'),
    },
    {
      heading: 'Modo DND',
      paragraphs: [
        'O modo DND ajuda quando você não quer ser interrompido. Ao ativá-lo, o aplicativo deixa de receber mensagens e notificações sem precisar ligar o modo avião.',
      ],
      image: require('@/assets/DND.webp'),
    },
    {
      heading: 'Mensagem em transmissão',
      paragraphs: [
        'Precisa enviar a mesma mensagem para várias pessoas? O recurso de transmissão permite mandar uma mensagem para diversos contatos de uma vez, sem criar um grupo. Algumas versões também incluem agendamento de mensagens.',
      ],
    },
    {
      heading: 'Baixar status',
      paragraphs: [
        'Salve fotos e vídeos dos status dos seus contatos direto na galeria, sem usar outro aplicativo para baixar. Depois de salvo, o arquivo fica disponível para acessar quando quiser.',
      ],
    },
    {
      heading: 'Compartilhamento de mídia maior',
      paragraphs: [
        'O WhatsApp GB aumenta os limites de envio de arquivos em comparação ao app oficial. Dependendo da versão, é possível enviar vídeos, áudios, documentos e mais fotos em uma única mensagem, mantendo melhor qualidade de imagem.',
      ],
    },
    {
      heading: 'Filtros de mensagens',
      paragraphs: [
        'Encontre conversas importantes mais rápido usando filtros de chats e mensagens. Isso ajuda a localizar mensagens não lidas, contatos específicos ou conversas em listas movimentadas.',
      ],
      image: require('@/assets/message-filter.png'),
    },
    {
      heading: 'Temas e emojis',
      paragraphs: [
        'Personalize o aplicativo com temas, fontes, papéis de parede e opções extras de emoji. A interface pode ser ajustada sem afetar suas conversas ou mídias.',
      ],
      image: require('@/assets/themes-4k-gbwhatsdroid.webp'),
    },
    {
      heading: 'Privacidade no WhatsApp GB',
      paragraphs: [
        'Muitos usuários escolhem o WhatsApp GB por causa das opções extras de privacidade. Em algumas versões, é possível ocultar o status online, congelar o visto por último, desativar indicadores de digitação ou controlar atividades específicas.',
      ],
    },
    {
      heading: 'Bloqueio do aplicativo',
      paragraphs: [
        'Proteja suas conversas com bloqueio integrado. Se o aparelho permitir, você pode abrir o app com PIN, senha ou impressão digital sem instalar outro bloqueador.',
      ],
    },
    {
      heading: 'Suporte a vários idiomas',
      paragraphs: [
        'O aplicativo oferece suporte a vários idiomas, facilitando o uso em diferentes regiões. Basta selecionar o idioma preferido nas configurações, caso esteja disponível na versão instalada.',
      ],
    },
    {
      heading: 'Congelar visto por último',
      paragraphs: [
        'Algumas versões permitem manter o visto por último em um horário anterior. Assim, você controla melhor o que outras pessoas veem enquanto continua usando o app.',
      ],
    },
    {
      heading: 'Compartilhar localização ao vivo',
      paragraphs: [
        'Compartilhe sua localização em tempo real com amigos ou familiares durante uma conversa. O recurso é útil para encontros ou para informar onde você está.',
      ],
    },
    {
      heading: 'Modo claro e modo escuro',
      paragraphs: [
        'Alterne entre tema claro e escuro conforme sua preferência ou as configurações do celular. Também é possível combinar esses modos com temas personalizados.',
      ],
    },
    {
      heading: 'Encaminhar mensagens sem etiqueta',
      paragraphs: [
        'Algumas versões incluem a opção de encaminhar mensagens sem exibir a etiqueta "Encaminhada". A disponibilidade pode variar conforme a versão.',
      ],
      image: require('@/assets/disable-forwarded.png'),
    },
    {
      heading: 'Atualizações regulares',
      paragraphs: [
        'Novas versões do WhatsApp GB costumam trazer correções de bugs, melhorias de compatibilidade e atualizações de recursos. Manter o app atualizado pode melhorar a estabilidade e reduzir problemas de versões antigas.',
      ],
    },
  ],
  whatsNew: [
    'Mais opções para gerenciar conteúdos salvos.',
    'Suporte a vários idiomas e tradução de conversas.',
    'Opção de suporte no site para ajudar com dúvidas.',
    'Controles de privacidade, como ocultar visualização de status e congelar visto por último.',
    'Processo de restauração de backup para trocar de aparelho.',
    'Acesso à loja de emojis e papéis de parede.',
    'Melhor gerenciamento de armazenamento e emojis baixados.',
  ],
  androidSteps: [
    {
      title: 'Passo 1: Ative fontes desconhecidas',
      text: 'Antes de instalar arquivos APK, habilite a opção Fontes desconhecidas nas configurações do dispositivo.',
    },
    {
      title: 'Passo 2: Acesse uma fonte confiável',
      text: 'Pesquise por baixar WhatsApp GB em um site confiável, pois o aplicativo não está disponível oficialmente no Google Play.',
    },
    {
      title: 'Passo 3: Baixe e instale',
      text: 'Abra a página de download, toque no botão para baixar e localize o arquivo na pasta de downloads. Toque no APK para iniciar a instalação.',
    },
    {
      title: 'Passo 4: Abra e verifique',
      text: 'Depois de instalar, abra o aplicativo e verifique seu número com o código OTP de 6 dígitos.',
    },
  ],
  iosSteps: [
    'Abra o Safari ou outro navegador e procure o arquivo IPA.',
    'Escolha apenas sites de terceiros confiáveis e procure o botão para baixar.',
    'Toque em Baixar para salvar o arquivo no dispositivo.',
    'Abra o arquivo e inicie a instalação.',
    'Em alguns minutos, o ícone do aplicativo aparecerá na tela.',
    'Abra o app e verifique com seu número de telefone.',
  ],
  pcSteps: [
    'Baixe e instale um emulador Android no PC.',
    'Baixe o arquivo WhatsApp GB Pro de uma fonte confiável e abra pelo emulador.',
    'Use LD Player, NOx ou BlueStacks para instalar no PC.',
    'Arraste o arquivo APK para a janela do emulador para concluir a instalação.',
    'Depois de instalar o app, verifique o número com o código OTP de 6 dígitos.',
    'Abra o aplicativo e use os recursos de privacidade e personalização disponíveis.',
    'Você também pode escanear o QR code do WhatsApp Web para usar a versão web.',
  ],
  backupSteps: [
    'Abra o WhatsApp GB e acesse as configurações pelo ícone de três pontos no canto superior direito.',
    'Procure <strong>"GB Settings"</strong>, toque nessa opção e entre nas configurações do app.',
    'Toque em Backup, abra <strong>"Chat Backup"</strong> e selecione a opção.',
    'Configure o backup automático de acordo com sua necessidade: diário, semanal ou mensal.',
    'Ative o backup automático para facilitar a recuperação de conversas em troca de aparelho ou problema na conta.',
  ],
  updateSteps: [
    'Abra as configurações do WhatsApp GB APK no aplicativo',
    'Encontre o botão Updates e toque em Check for updates',
    'Se houver atualização, toque em Baixar para obter a versão mais recente',
    'Instale a nova versão por cima da versão antiga que já está no dispositivo',
    'Se a atualização não aparecer, visite este site para baixar a nova versão do app',
  ],
  safetyTips: [
    'Baixe o APK de uma fonte em que você confia.',
    'Instale a versão mais recente em vez de versões antigas.',
    'Faça backup das conversas antes de atualizar ou trocar de versão.',
    'Mantenha o Google Play Protect ativado, se possível.',
    'Evite APKs modificados por fontes desconhecidas.',
  ],
  comparisonRows: [
    {
      label: 'Personalização',
      gb: 'Temas, papéis de parede e fontes personalizadas',
      official: 'Opções limitadas de tema claro e escuro',
    },
    {
      label: 'Opções de privacidade',
      gb: 'Congelar online, ocultar tique azul e esconder indicador de digitação',
      official: 'Privacidade básica',
    },
    {
      label: 'Duas contas',
      gb: 'Sim',
      official: 'Geralmente uma conta',
    },
    {
      label: 'Compartilhamento de mídia',
      gb: 'Suporte para até 200 imagens',
      official: 'Cerca de 30 imagens por envio',
    },
    {
      label: 'Tamanho do status',
      gb: '255 caracteres',
      official: '139 caracteres',
    },
    {
      label: 'Bloqueio do app',
      gb: 'Integrado',
      official: 'Usa recursos do sistema ou apps externos',
    },
    {
      label: 'Atualizações',
      gb: 'Atualizações manuais',
      official: 'Atualizações automáticas',
    },
    {
      label: 'Segurança',
      gb: 'Existe risco de restrição da conta',
      official: 'Mais seguro por ser oficial',
    },
  ],
  pros: [
    {
      icon: 'fa-cogs',
      text: 'Muitas opções de personalização disponíveis',
    },
    {
      icon: 'fa-palette',
      text: 'Permite alterar papéis de parede, temas e fontes',
    },
    {
      icon: 'fa-lock',
      text: 'Bloqueio do app para proteger dados pessoais',
    },
    {
      icon: 'fa-history',
      text: 'Congela o visto por último em uma data e hora específicas',
    },
    {
      icon: 'fa-check-double',
      text: 'Atualizações ajudam a corrigir bugs e problemas',
    },
  ],
  cons: [
    {
      icon: 'fa-ban',
      text: 'Pode haver risco de restrição temporária da conta',
    },
    {
      icon: 'fa-exclamation-triangle',
      text: 'Não segue as políticas oficiais do WhatsApp',
    },
    {
      icon: 'fa-times-circle',
      text: 'Não está disponível na Google Play Store',
    },
  ],
  faqs: [
    {
      question: 'Posso usar o WhatsApp GB e o WhatsApp oficial no mesmo aparelho?',
      answer:
        'Sim, o WhatsApp GB permite usar duas contas. Você pode manter o WhatsApp GB e o WhatsApp oficial no mesmo dispositivo.',
    },
    {
      question: 'O WhatsApp GB oferece chamadas com criptografia de ponta a ponta?',
      answer:
        'O WhatsApp GB usa serviços do WhatsApp, mas recursos modificados podem afetar a segurança. Para proteção completa, o aplicativo oficial continua sendo a opção mais indicada.',
    },
    {
      question: 'Posso transferir minhas conversas do WhatsApp oficial para o WhatsApp GB?',
      answer:
        'Sim, é possível fazer backup das conversas no WhatsApp oficial e restaurá-las ao instalar o APK. Confirme se o backup foi concluído para evitar perda de chats e mídias.',
    },
  ],
};

const ES_CONTENT = {
  features: [
    {
      title: 'Ocultar estado en línea',
      text: 'Mantente activo sin mostrar cuándo estás en línea.',
      icon: 'fa-shield-alt',
      theme: 'green',
    },
    {
      title: 'Descargador de estados',
      text: 'Guarda fotos y videos de los estados de WhatsApp directamente en tu teléfono.',
      icon: 'fa-download',
      theme: 'blue',
    },
    {
      title: 'Compartir archivos más grandes',
      text: 'Comparte fotos, videos y documentos de mayor tamaño con más facilidad.',
      icon: 'fa-file-image',
      theme: 'orange',
    },
    {
      title: 'Respuesta automática',
      text: 'Envía respuestas automáticas cuando estés ocupado o lejos del teléfono.',
      icon: 'fa-reply',
      theme: 'teal',
    },
    {
      title: 'Temas y personalización',
      text: 'Cambia colores, iconos, fuentes y la apariencia del chat más allá de las opciones de la app oficial.',
      icon: 'fa-palette',
      theme: 'purple',
    },
    {
      title: 'Soporte para dos cuentas',
      text: 'Usa una segunda cuenta de WhatsApp en el mismo dispositivo Android.',
      icon: 'fa-clone',
      theme: 'indigo',
    },
  ],
  appInfo: [
    { label: 'Nombre de la aplicación', value: 'GB WhatsApp' },
    { label: 'Nombre del paquete', value: 'com.gbwhatsapp' },
    { label: 'Versión', value: 'Más reciente' },
    { label: 'Tamaño del archivo', value: '90 MB' },
    { label: 'Requiere Android', value: '5.0+' },
    { label: 'Desarrollador / Mod', value: 'No oficial' },
    { label: 'Categoría', value: 'Comunicación' },
    { label: 'Licencia', value: 'Gratis (mod no oficial)' },
    { label: 'Última actualización', value: 'Hace 2 días' },
    { label: 'Tipo de archivo', value: 'APK' },
    {
      label: 'Permisos',
      value: 'Contactos, almacenamiento, cámara, micrófono, red, notificaciones',
    },
    { label: 'Soporte multicuenta', value: 'Sí' },
    { label: 'Aplicación base', value: 'WhatsApp Messenger (com.whatsapp)' },
    {
      label: 'Notas',
      value: 'Mod no oficial; no afiliado a WhatsApp. Úsalo bajo tu propia responsabilidad.',
    },
  ],
  featureSections: [
    {
      heading: 'Opciones de privacidad mejoradas',
      paragraphs: [
        `${pageLink(PAGE_LINKS.download, 'GB WhatsApp Pro', true)} mejora tu privacidad con funciones potentes, como ocultar los checks azules, desactivar el indicador de escritura, apagar el segundo check y reproducir notas de voz sin marcas. Puedes cambiar los checks azules a negros y ver estados y mensajes sin mostrar la vista. Mejora la privacidad según tu configuración.`,
      ],
      image: require('@/assets/hide-blueticks.webp'),
    },
    {
      heading: 'DND',
      paragraphs: [
        'Este modo sirve para no ser molestado durante tus momentos ocupados. Solo desactiva la app sin activar el modo avión. Cuando activas este modo, no puedes recibir mensajes ni notificaciones de la aplicación. Cuando quieras interactuar con alguien, desactiva este modo o vuelve a recibir notificaciones.',
      ],
      image: require('@/assets/DND.webp'),
    },
    {
      heading: 'Mensajes de difusión',
      paragraphs: [
        '¿Necesitas enviar el mismo mensaje a varias personas? La función de difusión integrada te permite enviar un mensaje a varios contactos a la vez sin crear un grupo. Algunas versiones también incluyen programación de mensajes, para elegir una hora específica y enviarlos automáticamente.',
      ],
    },
    {
      heading: 'Descarga de estados',
      paragraphs: [
        'Guarda fotos y videos de las actualizaciones de estado de tus amigos directamente en tu teléfono sin usar un descargador separado. Una vez descargados, los archivos se guardan en tu galería para acceder a ellos fácilmente más tarde.',
      ],
    },
    {
      heading: 'Compartir archivos multimedia más grandes',
      paragraphs: [
        'GB WhatsApp aumenta los límites para compartir archivos en comparación con la aplicación oficial. Según la versión, puedes enviar videos más grandes, archivos de audio, documentos y más fotos en un solo mensaje, manteniendo una mejor calidad de imagen.',
      ],
    },
    {
      heading: 'Filtros de mensajes',
      paragraphs: [
        'Encuentra conversaciones importantes más rápido filtrando chats y mensajes. Esto puede facilitar la localización de mensajes no leídos, contactos específicos o conversaciones en listas de chats con mucha actividad.',
      ],
      image: require('@/assets/message-filter.png'),
    },
    {
      heading: 'Temas y emojis',
      paragraphs: [
        'Personaliza la aplicación con temas, fuentes, fondos de pantalla y opciones adicionales de emojis. Puedes cambiar la apariencia de la interfaz sin afectar tus chats ni tus archivos multimedia.',
      ],
      image: require('@/assets/themes-4k-gbwhatsdroid.webp'),
    },
    {
      heading: 'Opciones de privacidad',
      paragraphs: [
        'Muchos usuarios eligen GB WhatsApp por sus ajustes adicionales de privacidad. Según la versión, es posible ocultar tu estado en línea, congelar la última vez, desactivar indicadores de escritura o controlar quién puede ver ciertas actividades.',
      ],
    },
    {
      heading: 'Bloqueo de la aplicación',
      paragraphs: [
        'Protege tus chats con un bloqueo integrado. Si tu dispositivo lo permite, puedes desbloquear la app con PIN, contraseña o huella digital sin instalar otra aplicación de bloqueo.',
      ],
    },
    {
      heading: 'Múltiples idiomas',
      paragraphs: [
        'La aplicación admite varios idiomas, lo que facilita su uso en diferentes regiones. Solo selecciona tu idioma preferido desde los ajustes si está disponible en tu versión.',
      ],
    },
    {
      heading: 'Congelar última vez',
      paragraphs: [
        'En lugar de mostrar tu actividad actual, algunas versiones permiten mantener tu última vez en un horario anterior. Esto te da más control sobre lo que otras personas pueden ver mientras sigues usando la app.',
      ],
    },
    {
      heading: 'Compartir ubicación en vivo',
      paragraphs: [
        'Comparte tu ubicación en tiempo real con amigos o familiares durante un chat. Puede ser útil cuando vas a encontrarte con alguien o quieres avisar dónde estás.',
      ],
    },
    {
      heading: 'Modo oscuro y modo claro',
      paragraphs: [
        'Cambia entre temas claros y oscuros para ajustarlos a tus preferencias o a la configuración del sistema del teléfono. También puedes combinarlos con temas personalizados para una interfaz más personal.',
      ],
    },
    {
      heading: 'Reenviar mensajes sin etiqueta',
      paragraphs: [
        'Algunas versiones incluyen una opción para reenviar mensajes sin mostrar la etiqueta "Reenviado". La disponibilidad de la función puede variar según la versión.',
      ],
      image: require('@/assets/disable-forwarded.png'),
    },
    {
      heading: 'Actualizaciones regulares',
      paragraphs: [
        'Las nuevas versiones de GB WhatsApp suelen incluir correcciones de errores, mejoras de compatibilidad y actualizaciones de funciones. Mantener la app actualizada puede mejorar la estabilidad y reducir problemas que aparecen al usar versiones antiguas.',
      ],
    },
  ],
  whatsNew: [
    'Hay muchas opciones disponibles para gestionar el contenido descargado.',
    'Puedes usar la aplicación en varios idiomas y traducir chats.',
    'Hay una opción de atención al usuario disponible en el sitio web para resolver problemas.',
    'Controla tu privacidad social con opciones como ocultar la vista de estados y congelar la última vez.',
    'Usa el proceso de restauración de copia de seguridad para cambiar de dispositivo.',
    'Usa libremente la tienda de emojis y la tienda de fondos de pantalla.',
    'Hay una opción para mejorar la gestión del almacenamiento y de los emojis descargados.',
  ],
  androidSteps: [
    {
      title: 'Paso 1: Activa fuentes desconocidas',
      text: 'Para descargar archivos APK en tu dispositivo, activa la opción Fuentes desconocidas en los ajustes.',
    },
    {
      title: 'Paso 2: Visita una fuente confiable',
      text: 'Busca Descargar GB WhatsApp en Google; no está disponible oficialmente. Puedes descargarlo desde un sitio web confiable.',
    },
    {
      title: 'Paso 3: Descarga e instala',
      text: 'Abre el sitio de búsqueda, localiza el botón de descarga en la página principal y haz clic en él. El archivo se guarda en la carpeta de descargas del dispositivo. Haz clic en el archivo y la instalación comenzará al instante.',
    },
    {
      title: 'Paso 4: Abre y disfruta',
      text: 'Después de abrirla, toca el icono y abre la aplicación para verificar tu número con un OTP de 6 dígitos.',
    },
  ],
  iosSteps: [
    'Ve a Safari o a cualquier otro navegador y busca el archivo IPA.',
    'Selecciona solo sitios de terceros confiables, ábrelos y busca el botón de descarga.',
    'Haz clic en el botón Descargar para guardar el archivo en tu dispositivo.',
    'Haz clic en el archivo y comenzará la instalación.',
    'En pocos minutos, el icono de la aplicación aparecerá en la pantalla de tu dispositivo.',
    'Ábrela y verifica con tu número de teléfono.',
  ],
  pcSteps: [
    'Descarga e instala un emulador en tu PC.',
    'Descarga el archivo GB WhatsApp Pro desde una fuente confiable y ábrelo con el emulador.',
    'Usa LD Player, NOx o BlueStacks para la instalación en PC.',
    'Arrastra y suelta el archivo APK en el emulador para completar el proceso de instalación.',
    'Cuando instales la aplicación y verifiques el OTP de 6 dígitos, el icono aparecerá en la ventana del PC.',
    'Abre la aplicación y disfruta de la personalización de privacidad y las funciones avanzadas de la app.',
    'También puedes escanear el código QR de WhatsApp Web para usar la app basada en web.',
  ],
  backupSteps: [
    'Inicia GB WhatsApp y abre los ajustes tocando los tres puntos en la parte superior derecha.',
    'Después de buscar, encontrarás <strong>"GB Settings"</strong>; toca esa opción y ve a los subajustes de la app.',
    'Toca la opción Backup, entra en <strong>"Chat Backup"</strong> y selecciónala.',
    'Después de tocar Chat Backup, puedes configurar la copia automática de tus datos según copias diarias, semanales o mensuales.',
    'También puedes activar la copia automática. Hará copias frecuentes de tus chats y guardará tus datos ante un bloqueo de cuenta o cambio de dispositivo.',
  ],
  updateSteps: [
    'Abre los ajustes de GB WhatsApp APK en la aplicación',
    'Busca el botón Updates y toca la opción Check for updates',
    'Si hay actualizaciones disponibles, haz clic en el botón Descargar para obtener la última versión',
    'Instala la nueva versión sobre la versión anterior que ya tienes en tu dispositivo',
    'Si no ves una actualización disponible, visita nuestro sitio y consigue la nueva versión de la app',
  ],
  safetyTips: [
    'Descarga el APK desde una fuente en la que confíes.',
    'Instala la última versión disponible en lugar de versiones antiguas.',
    'Haz una copia de seguridad de tus chats antes de actualizar o cambiar de versión.',
    'Mantén Google Play Protect activado si es posible.',
    'Evita archivos APK modificados por fuentes desconocidas.',
  ],
  comparisonRows: [
    {
      label: 'Personalización',
      gb: 'Temas, fondos de pantalla y fuentes personalizados',
      official: 'Opciones limitadas de modo oscuro y claro',
    },
    {
      label: 'Opciones de privacidad',
      gb: 'Congelar estado en línea, ocultar checks azules, ocultar indicador de escritura',
      official: 'Privacidad básica',
    },
    {
      label: 'Dos cuentas',
      gb: 'Sí',
      official: 'Solo una cuenta',
    },
    {
      label: 'Compartir multimedia',
      gb: 'Soporta hasta 200 imágenes',
      official: 'Envía solo 30 imágenes a la vez',
    },
    {
      label: 'Longitud del estado',
      gb: '255 caracteres',
      official: '139 caracteres',
    },
    {
      label: 'Bloqueo de app',
      gb: 'Integrado',
      official: 'Usa opciones externas',
    },
    {
      label: 'Actualizaciones',
      gb: 'Actualizaciones manuales',
      official: 'Actualizaciones automáticas',
    },
    {
      label: 'Seguridad',
      gb: 'Riesgo menor de bloqueo de cuenta',
      official: 'Totalmente segura',
    },
  ],
  pros: [
    {
      icon: 'fa-cogs',
      text: 'Muchas opciones de personalización disponibles',
    },
    {
      icon: 'fa-palette',
      text: 'Cambia fondos de pantalla, temas y estilos de fuente',
    },
    {
      icon: 'fa-lock',
      text: 'Bloquea tu app y mantén los datos privados',
    },
    {
      icon: 'fa-history',
      text: 'Congela tu última vez con una fecha y hora específicas',
    },
    {
      icon: 'fa-check-double',
      text: 'Se mantiene actualizada con correcciones de errores y problemas',
    },
  ],
  cons: [
    {
      icon: 'fa-ban',
      text: 'Riesgo menor de bloqueo temporal de la cuenta',
    },
    {
      icon: 'fa-exclamation-triangle',
      text: 'Va contra las políticas oficiales de WhatsApp',
    },
    {
      icon: 'fa-times-circle',
      text: 'No está disponible en Google Play Store',
    },
  ],
  faqs: [
    {
      question: '¿Puedo usar GB WhatsApp y WhatsApp oficial en el mismo dispositivo?',
      answer:
        'Sí, GB WhatsApp permite dos cuentas. Puedes usar GB WhatsApp y WhatsApp oficial al mismo tiempo en tu dispositivo.',
    },
    {
      question: '¿GB WhatsApp admite llamadas cifradas de extremo a extremo?',
      answer:
        'GB WhatsApp usa los servidores de WhatsApp. Las funciones modificadas pueden afectar la seguridad. WhatsApp oficial admite llamadas cifradas de extremo a extremo, pero GB WhatsApp no puede ofrecer el mismo tipo de calidad.',
    },
    {
      question: '¿Puedo transferir mis chats de WhatsApp oficial a GB WhatsApp?',
      answer:
        'Sí, puedes hacer una copia de seguridad de tus chats desde WhatsApp oficial y restaurarla al instalar la aplicación APK. Deberías evitar perder tus chats y archivos multimedia.',
    },
  ],
};

const LOCALIZED_CONTENT = {
  id: ID_CONTENT,
  es: ES_CONTENT,
  br: BR_CONTENT,
};

export default {
  name: 'Home',
  components: {
    HeadNav,
  },
  data() {
    return {
      site,
      openFaqIndexes: [],
      pageLinks: PAGE_LINKS,
      downloadStatus: {
        title: 'Latest GB WhatsApp APK Available',
        details: [
          { label: 'Version', value: 'GB WhatsApp v18.80' },
          { label: 'Platform', value: 'Android' },
          { label: 'Android Requirement', value: 'Android 5.0 or above' },
          { label: 'Availability', value: 'Available for manual APK installation' },
        ],
        securityCheck: {
          label: 'Security Check',
          value: 'Scan the APK before installation',
        },
        virusTotal:
          `Before installing the APK, you can check the downloaded GB WhatsApp file with VirusTotal. After downloading from ${pageLink(PAGE_LINKS.home, 'GB')}${pageLink(PAGE_LINKS.home, 'whatsdroid.com')}, upload the APK to VirusTotal and review the scan results for potential threats. Users should always make their own judgment before installing any third-party APK.`,
      },
      userChoiceSections: [
        {
          heading: 'More Privacy Control',
          paragraphs: [
            'Some users prefer to keep their online activity less visible or have more control over read receipts and other activity indicators. GB WhatsApp provides additional settings for users who want greater control over their privacy.',
          ],
        },
        {
          heading: 'More Personalization',
          paragraphs: [
            'The standard WhatsApp interface offers limited customization. Users who want to change themes, fonts, colors, wallpapers, or other parts of the chat interface may prefer a more customizable alternative.',
          ],
        },
        {
          heading: 'Fewer Sharing Restrictions',
          paragraphs: [
            'Sending large videos, photos, and documents can be inconvenient when standard limits get in the way. Some users look for GB WhatsApp because they want more flexibility when sharing media with friends, family, or work contacts.',
          ],
        },
        {
          heading: 'Easier Everyday Messaging',
          paragraphs: [
            'Users who receive messages while busy may want more control over how they respond and manage conversations. Options such as automatic replies and message scheduling can make routine communication more convenient.',
          ],
        },
        {
          heading: 'A Different WhatsApp Experience',
          paragraphs: [
            'For many users, the appeal is simply having more choices than the standard app provides. GB WhatsApp combines additional privacy, customization, and messaging controls in a single Android application.',
          ],
        },
      ],
      features: [
        {
          title: 'Hide online status',
          text: 'Stay active without showing when you are online.',
          icon: 'fa-shield-alt',
          theme: 'green',
        },
        {
          title: 'Status downloader',
          text: 'Save photos and videos from WhatsApp Status directly to your phone.',
          icon: 'fa-download',
          theme: 'blue',
        },
        {
          title: 'Larger media sharing',
          text: 'Share higher-size photos, videos, and documents more easily.',
          icon: 'fa-file-image',
          theme: 'orange',
        },
        {
          title: 'Auto reply',
          text: 'Send automatic responses when you are busy or away from your phone.',
          icon: 'fa-reply',
          theme: 'teal',
        },
        {
          title: 'Themes and customization',
          text: 'Change colors, icons, fonts, and chat appearance beyond the official app options.',
          icon: 'fa-palette',
          theme: 'purple',
        },
        {
          title: 'Dual account support',
          text: 'Use a second WhatsApp account on the same Android device.',
          icon: 'fa-clone',
          theme: 'indigo',
        },
      ],
      appInfo: [
        { label: 'App Name', value: 'GB WhatsApp' },
        { label: 'Package Name', value: 'com.gbwhatsapp' },
        { label: 'Version', value: 'Latest' },
        { label: 'File Size', value: '90 MB' },
        { label: 'Requires Android', value: '5.0+' },
        { label: 'Developer / Mod', value: 'Unofficial' },
        { label: 'Category', value: 'Communication' },
        { label: 'License', value: 'Free (unofficial mod)' },
        { label: 'Last Updated', value: '2 Days Ago' },
        { label: 'File Type', value: 'APK' },
        {
          label: 'Permissions',
          value: 'Contacts, Storage, Camera, Microphone, Network, Notifications',
        },
        { label: 'Multi-Account Support', value: 'Yes' },
        { label: 'Base App', value: 'WhatsApp Messenger (com.whatsapp)' },
        {
          label: 'Notes',
          value: 'Unofficial mod; not affiliated with WhatsApp. Use at your own risk.',
        },
      ],
      featureSections: [
        {
          heading: 'Enhanced Privacy Options',
          paragraphs: [
            `${pageLink(PAGE_LINKS.download, 'GB WhatsApp Pro', true)} enhances your privacy with powerful features such as hiding blue ticks, typing indication disable, second tick off, and playing voice notes without ticks. You can change the blue ticks into black and see status and messages without showing view. It enhances privacy according to your settings.`,
          ],
          image: require('@/assets/hide-blueticks.webp'),
        },
        {
          heading: 'DND',
          paragraphs: [
            'This mode carries with do not disturb during your busy time. Just turn off your app without enabling airplane mode. When you enable the mode, you cannot receive any messages or notifications from the app. When you want to interact with someone, turn off this mode or receive notifications.',
          ],
          image: require('@/assets/DND.webp'),
        },
        {
          heading: 'Broadcast Messaging',
          paragraphs: [
            'Need to send the same message to multiple people? The built-in broadcast feature lets you send one message to several contacts at once without creating a group. Some versions also include message scheduling, so you can choose a specific time for messages to be sent automatically.',
          ],
        },
        {
          heading: 'Status Download',
          paragraphs: [
            'Save photos and videos from your friends\' status updates directly to your phone without using a separate downloader. Once downloaded, the files are stored in your gallery for easy access later.',
          ],
        },
        {
          heading: 'Larger Media Sharing',
          paragraphs: [
            'GB WhatsApp increases the file-sharing limits compared to the official app. Depending on the version, you can send larger videos, audio files, documents, and more photos in a single message while keeping better image quality.',
          ],
        },
        {
          heading: 'Message Filters',
          paragraphs: [
            'Find important conversations more quickly by filtering chats and messages. This can make it easier to locate unread messages, specific contacts, or conversations in busy chat lists.',
          ],
          image: require('@/assets/message-filter.png'),
        },
        {
          heading: 'Themes and Emoji',
          paragraphs: [
            'Personalize the app with custom themes, fonts, wallpapers, and additional emoji options. You can change the appearance of the interface without affecting your chats or media.',
          ],
          image: require('@/assets/themes-4k-gbwhatsdroid.webp'),
        },
        {
          heading: 'Privacy Options',
          paragraphs: [
            'Many users choose GB WhatsApp because of its extra privacy settings. Depending on the version, you may be able to hide your online status, freeze your last seen, disable typing indicators, or control who can see certain activities.',
          ],
        },
        {
          heading: 'App Lock',
          paragraphs: [
            'Protect your chats with a built-in app lock. If your device supports it, you can unlock the app using a PIN, password, or fingerprint without installing another locking app.',
          ],
        },
        {
          heading: 'Multiple Languages',
          paragraphs: [
            'The app supports multiple languages, making it easier to use in different regions. Simply select your preferred language from the settings if it is available in your version.',
          ],
        },
        {
          heading: 'Freeze Last Seen',
          paragraphs: [
            'Instead of showing your current activity, some versions allow you to keep your last seen at an earlier time. This gives you more control over what other people can see while you continue using the app.',
          ],
        },
        {
          heading: 'Live Location Sharing',
          paragraphs: [
            'Share your real-time location with friends or family during a chat. This can be useful when meeting someone or letting others know where you are.',
          ],
        },
        {
          heading: 'Dark Mode and Light Mode',
          paragraphs: [
            'Switch between light and dark themes to match your preference or your phone\'s system settings. You can also combine these with custom themes for a more personalized interface.',
          ],
        },
        {
          heading: 'Forward Messages Without Label',
          paragraphs: [
            'Some versions include an option to forward messages without displaying the "Forwarded" label. Feature availability may vary depending on the release.',
          ],
          image: require('@/assets/disable-forwarded.png'),
        },
        {
          heading: 'Regular Updates',
          paragraphs: [
            'New versions of GB WhatsApp often include bug fixes, compatibility improvements, and feature updates. Keeping the app up to date may help improve stability and reduce issues that can occur when using older releases.',
          ],
        },
      ],
      whatsNew: [
        '<strong>Bug fixes:</strong> Fixed crashes, freezing, and other common issues.',
        '<strong>Better performance:</strong> Reduced lag and improved loading speed.',
        '<strong>Android compatibility:</strong> Improved support for newer Android versions and devices.',
        '<strong>Backup fixes:</strong> Improved backup and restore reliability.',
        '<strong>Media fixes:</strong> Resolved problems with photos, videos, and documents not loading correctly.',
        '<strong>Stability improvements:</strong> General fixes make everyday use smoother and more reliable.',
      ],
      androidSteps: [
        {
          title: 'Step 1: Enable Unknown Sources',
          text: 'To download APK files on your device, enable the Unknown Sources option in settings.',
        },
        {
          title: 'Step 2: Visit a Trusted Source',
          text: 'Search for GB WhatsApp Download on Google; it is not available officially. You can download it from a trusted website.',
        },
        {
          title: 'Step 3: Download and Install',
          text: 'Open the search site, locate the download button on the home page, and click on it. The file is saved in the device downloads folder. Click on the file, and it starts installing instantly.',
        },
        {
          title: 'Step 4: Launch and Enjoy',
          text: 'After launch, click the icon and open the app to verify your number with a 6-digit OTP.',
        },
      ],
      iosSteps: [
        'Go to Safari or any other browser and search for the IPA file.',
        'Select only trusted third-party sites, open them, and look for the download button.',
        'Click the Download button to save the file on your device.',
        'Click on the file, and the file starts installing.',
        'Within a few minutes, the application icon will be shown on your device screen.',
        'Open it and verify with your phone number.',
      ],
      pcSteps: [
        'Download and install an emulator on your PC.',
        'Download the GB WhatsApp Pro file from a trustworthy source and open it with the emulator.',
        'Use LD Player, NOx, or BlueStacks for PC installation.',
        'Drag and drop the APK file into the emulator to complete the installation process.',
        'When you install the App and verify the 6-digit OTP, the icon displays on the PC window.',
        'Open the application and enjoy privacy customization and advanced App assets.',
        'You can also scan the WhatsApp Web QR code to use the web-based App.',
      ],
      backupSteps: [
        'Launch GB WhatsApp and open the settings by clicking the three dots on the top right side.',
        'After searching, you will find the <strong>"GB Settings"</strong>, click on that, and go to the sub-settings of the App.',
        'Click on the Backup option, go to the <strong>"Chat Backup"</strong> option, and tap it.',
        'After clicking Chat Backup, you can set auto-backup for your data, which is set according to the daily, weekly, and monthly backups created.',
        'You can also enable auto-backup. It\'ll back up your chats frequently and save your data in every account ban or device change situation.',
      ],
      updateSteps: [
        'Open GB WhatsApp APK settings in App',
        'Find the Updates button and tap on the Check for updates option',
        'If updates are available, click on the Download button for the latest version',
        'Install the new version over the old one that you already have on your device',
        'If you cannot see the update available, visit our site and get the new App version',
      ],
      notInstalling: [
        '<strong>Incompatible Android version:</strong> Check whether your Android version supports the APK, then download a compatible version.',
        '<strong>Incomplete or corrupted APK:</strong> Delete the current file and download the APK again from a trusted source.',
        '<strong>Not enough storage:</strong> Free up some storage space before starting the installation.',
        '<strong>Unknown sources disabled:</strong> Allow your browser or file manager to install apps from unknown sources in Android settings.',
        '<strong>Existing WhatsApp conflict:</strong> If another GB WhatsApp or WhatsApp version is installed, uninstall the conflicting version or use a compatible package.',
        '<strong>Old APK version:</strong> Download the latest compatible GB WhatsApp APK and try installing it again.',
      ],
      safetyTips: [
        'Download the APK from a source you trust.',
        'Install the latest available version instead of outdated releases.',
        'Back up your chats before updating or switching versions.',
        'Keep Google Play Protect enabled if possible.',
        'Avoid APK files that have been modified by unknown sources.',
      ],
      comparisonRows: [
        {
          label: 'Customization',
          gb: 'Customized themes, wallpapers, fonts',
          official: 'Limited Dark and Light',
        },
        {
          label: 'Privacy Options',
          gb: 'Freeze online status, Hide blue ticks, Hide typing indicator',
          official: 'Basic Privacy',
        },
        {
          label: 'Dual Accounts',
          gb: 'Yes',
          official: 'Only single Account',
        },
        {
          label: 'Media Sharing',
          gb: 'Supported up to 200 images',
          official: 'Send only 30 images at a time',
        },
        {
          label: 'Status Length',
          gb: '255 Characters',
          official: '139 Characters',
        },
        {
          label: 'App lock',
          gb: 'Built In',
          official: 'Use External',
        },
        {
          label: 'Updates',
          gb: 'Manual Updates',
          official: 'Auto Updates',
        },
        {
          label: 'Security',
          gb: 'Minor risk of Account Bann',
          official: 'Full Secure',
        },
      ],
      pros: [
        {
          icon: 'fa-cogs',
          text: 'Lots of customizations available',
        },
        {
          icon: 'fa-palette',
          text: 'Change wallpapers, themes, and font styles',
        },
        {
          icon: 'fa-lock',
          text: 'Lock your app and keep data private',
        },
        {
          icon: 'fa-history',
          text: 'Freeze your last seen with a specific date and time',
        },
        {
          icon: 'fa-check-double',
          text: 'Up to date with fixing bugs and issues',
        },
      ],
      cons: [
        {
          icon: 'fa-ban',
          text: 'Minor risk of a temporary ban on the account',
        },
        {
          icon: 'fa-exclamation-triangle',
          text: 'Illegal against the official WhatsApp policies',
        },
        {
          icon: 'fa-times-circle',
          text: 'Not available on the Google Play Store',
        },
      ],
      faqs: [
        {
          question: 'Can I use GB WhatsApp and official WhatsApp both in same device?',
          answer:
            'Yes, GB WhatsApp allows dual accounts. You can run GB WhatsApp and the official WhatsApp both same time on your device.',
        },
        {
          question: 'Is GB WhatsApp support end-to-end encrypted calls?',
          answer:
            'GB WhatsApp uses WhatsApp\'s servers officially. The modified features some affect security. The official WhatsApp supported full end-to-end encrypted calls, but GB WhatsApp cannot provide the same type of quality.',
        },
        {
          question: 'Can I transfer my chats from Official WhatsApp to GB WhatsApp?',
          answer:
            'Yes, you can back up your chats from the official WhatsApp and restore them when installing the APK app. You should lose your chat and media data.',
        },
        {
          question: 'Can GB WhatsApp be used on Android 15?',
          answer:
            'Compatibility depends on the GB WhatsApp build and device. If the APK does not install or keeps crashing on Android 15, using a newer compatible version may resolve the issue.',
        },
        {
          question: 'Why is GB WhatsApp not working?',
          answer:
            'Common causes include an outdated APK, installation conflicts, compatibility problems, corrupted data, or network issues. Updating the app or reinstalling a compatible version may help.',
        },
        {
          question: 'Can I restore a GB WhatsApp backup?',
          answer:
            'Yes, but the backup must be created and stored correctly. Before changing devices or reinstalling the app, make sure your important chats and media have been backed up.',
        },
        {
          question: 'What should I do if my GB WhatsApp account is banned?',
          answer:
            'If your account is temporarily restricted, follow the instructions shown by WhatsApp and consider switching back to the official app. Avoid repeatedly attempting to bypass account restrictions.',
        },
        {
          question: 'Can I download an older version of GB WhatsApp?',
          answer:
            'Yes, older APK versions may be available, but using an outdated build can cause compatibility, stability, or security problems. A current compatible version is generally preferable.',
        },
        {
          question: 'Why can\'t I download media on GB WhatsApp?',
          answer:
            'Check your internet connection, available storage space, and app permissions. If the problem continues, updating GB WhatsApp may help resolve compatibility or download issues.',
        },
        {
          question: 'Why does GB WhatsApp keep crashing?',
          answer:
            'Crashes may occur because of an incompatible version, corrupted app data, insufficient storage, or Android compatibility issues. Updating to a compatible build can often help.',
        },
      ],
    };
  },
  computed: {
    currentLanguage() {
      return this.$route.meta.language || 'en';
    },
    homeCopy() {
      return HOME_COPY[this.currentLanguage] || HOME_COPY.en;
    },
    pageText() {
      return PAGE_TEXT[this.currentLanguage] || PAGE_TEXT.en;
    },
    localizedContent() {
      return LOCALIZED_CONTENT[this.currentLanguage] || {};
    },
    downloadStatusContent() {
      if (this.currentLanguage !== 'en') {
        return {};
      }
      return this.downloadStatus;
    },
    userChoiceSectionItems() {
      if (this.currentLanguage !== 'en') {
        return [];
      }
      return this.userChoiceSections;
    },
    featureCards() {
      return this.localizedContent.features || this.features;
    },
    appInfoRows() {
      return this.localizedContent.appInfo || this.appInfo;
    },
    featureContentSections() {
      return this.localizedContent.featureSections || this.featureSections;
    },
    whatsNewItems() {
      return this.localizedContent.whatsNew || this.whatsNew;
    },
    androidStepItems() {
      return this.localizedContent.androidSteps || this.androidSteps;
    },
    iosStepItems() {
      return this.localizedContent.iosSteps || this.iosSteps;
    },
    pcStepItems() {
      return this.localizedContent.pcSteps || this.pcSteps;
    },
    backupStepItems() {
      return this.localizedContent.backupSteps || this.backupSteps;
    },
    updateStepItems() {
      return this.localizedContent.updateSteps || this.updateSteps;
    },
    notInstallingItems() {
      if (this.currentLanguage !== 'en') {
        return [];
      }
      return this.notInstalling;
    },
    safetyTipItems() {
      return this.localizedContent.safetyTips || this.safetyTips;
    },
    comparisonTableRows() {
      return this.localizedContent.comparisonRows || this.comparisonRows;
    },
    proItems() {
      return this.localizedContent.pros || this.pros;
    },
    conItems() {
      return this.localizedContent.cons || this.cons;
    },
    faqItems() {
      return this.localizedContent.faqs || this.faqs;
    },
  },
  methods: {
    gotodownload() {
      if (typeof window !== 'undefined') {
        window.location.href = this.site.apkUrl;
      }
    },
    toggleFaq(index) {
      if (this.openFaqIndexes.includes(index)) {
        this.openFaqIndexes = this.openFaqIndexes.filter((item) => item !== index);
      } else {
        this.openFaqIndexes = [...this.openFaqIndexes, index];
      }
    },
    isFaqOpen(index) {
      return this.openFaqIndexes.includes(index);
    },
  },
};
</script>

<style lang="scss" scoped>
.home-page {
  min-height: 100vh;
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, rgba(4, 130, 74, 0.06), transparent 60%),
    linear-gradient(180deg, #f4f7fb 0%, #f8fafc 40%, #f1f5f9 100%);
}

.home-main {
  width: min(1120px, calc(100% - 30px));
  margin: 0 auto;
  padding: 32px 0 72px;
}

.home-hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas: "copy visual";
  gap: 16px 28px;
  align-items: center;
  padding: 32px 40px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  background: linear-gradient(135deg, #023d22 0%, #035e34 38%, #04824a 72%, #06a35a 100%);
  color: #fff;
  box-shadow:
    0 28px 64px rgba(2, 61, 34, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  isolation: isolate;
}

.hero-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.hero-bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
}

.hero-bg-orb--one {
  top: -80px;
  right: -40px;
  width: 320px;
  height: 320px;
  background: rgba(37, 211, 102, 0.35);
}

.hero-bg-orb--two {
  bottom: -100px;
  left: 20%;
  width: 260px;
  height: 260px;
  background: rgba(255, 255, 255, 0.08);
}

.hero-bg-grid {
  position: absolute;
  inset: 0;
  opacity: 0.35;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 32px 32px;
  mask-image: radial-gradient(ellipse 80% 70% at 50% 50%, #000 20%, transparent 100%);
}

.hero-copy,
.hero-visual {
  position: relative;
  z-index: 1;
}

.hero-copy {
  min-width: 0;
  grid-area: copy;
}

.security-marker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 18px;
  padding: 7px 14px 7px 12px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  font-size: 14px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.security-marker .fa {
  font-size: 13px;
  color: #7dffb0;
}

.hero-copy h1 {
  margin: 0;
  color: #fff;
  font-size: clamp(32px, 4.2vw, 50px);
  line-height: 1.12;
  letter-spacing: -0.02em;
  text-wrap: balance;
  text-shadow: 0 2px 24px rgba(0, 0, 0, 0.18);
}

.hero-text {
  margin: 16px 0 22px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 18px;
  line-height: 1.6;
}

.primary-download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 220px;
  min-height: 54px;
  padding: 14px 28px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  background: linear-gradient(180deg, #fff 0%, #f0faf4 100%);
  color: #0b6d47;
  font-size: 17px;
  font-weight: 800;
  text-decoration: none;
  box-shadow:
    0 10px 28px rgba(0, 0, 0, 0.18),
    0 2px 0 rgba(255, 255, 255, 0.6) inset;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.primary-download:hover {
  transform: translateY(-2px);
  background: #fff;
  box-shadow:
    0 16px 36px rgba(0, 0, 0, 0.22),
    0 2px 0 rgba(255, 255, 255, 0.7) inset;
}

.primary-download:active {
  transform: translateY(0);
}

.primary-download .fa {
  font-size: 1.05em;
}

.hero-note {
  margin: 14px 0 0;
  padding-left: 12px;
  border-left: 2px solid rgba(255, 255, 255, 0.22);
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
  line-height: 1.55;
}

.hero-visual {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  grid-area: visual;
}

.hero-phone {
  position: relative;
  display: inline-block;
  padding: 10px 9px 12px;
  border-radius: 34px;
  background: linear-gradient(165deg, #2f2f2f 0%, #141414 48%, #232323 100%);
  box-shadow:
    0 22px 44px rgba(0, 0, 0, 0.34),
    inset 0 1px 0 rgba(255, 255, 255, 0.14),
    inset 0 -1px 0 rgba(0, 0, 0, 0.45);
}

.hero-phone::before {
  position: absolute;
  top: 72px;
  right: -2px;
  width: 3px;
  height: 36px;
  border-radius: 0 2px 2px 0;
  background: linear-gradient(180deg, #3a3a3a, #1a1a1a);
  content: '';
  opacity: 0.85;
}

.hero-phone-screen {
  position: relative;
  overflow: hidden;
  border-radius: 26px;
  background: #050505;
  line-height: 0;
}

.hero-phone-screen::before {
  position: absolute;
  z-index: 2;
  top: 10px;
  left: 50%;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #3a3a3a, #0a0a0a 65%);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.06);
  content: '';
  transform: translateX(-50%);
}

.hero-phone-screen::after {
  position: absolute;
  z-index: 2;
  bottom: 8px;
  left: 50%;
  width: 88px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  content: '';
  transform: translateX(-50%);
}

.hero-phone-image {
  display: block;
  width: min(272px, calc(30vw - 18px));
  height: auto;
  object-fit: contain;
  filter: saturate(1.04) contrast(1.02) brightness(0.98);
}

.content-section {
  margin-top: 24px;
  padding: 32px 36px;
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.04);
}

.content-section:first-of-type {
  margin-top: 28px;
}

.content-section h2 {
  position: relative;
  margin: 0 0 20px;
  padding-bottom: 14px;
  color: #14213d;
  font-size: 26px;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.content-section h2::after {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 44px;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, #04824a, #06a35a);
  content: '';
}

.content-section h3 {
  margin: 20px 0 10px;
  color: #1e293b;
  font-size: 19px;
  line-height: 1.35;
  letter-spacing: -0.01em;
}

.content-section p,
.text-block p {
  margin: 0 0 14px;
  color: #3c4b62;
  font-size: 16px;
  line-height: 1.78;
}

.content-section a,
.text-block a,
.step-list a,
.check-list a {
  color: #2563eb !important;
  font-weight: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.content-section a strong,
.text-block a strong,
.step-list a strong,
.check-list a strong {
  color: inherit;
  font-weight: inherit;
}

.content-section a:hover,
.text-block a:hover,
.step-list a:hover,
.check-list a:hover,
.content-section a:focus,
.text-block a:focus,
.step-list a:focus,
.check-list a:focus {
  color: #1d4ed8 !important;
}

.muted {
  margin-top: 18px;
  color: #64748b;
  font-size: 14px;
}

.download-status-details {
  display: grid;
  gap: 8px;
  margin: 18px 0;
  padding: 16px 18px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
}

.download-status-details p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
}

.quick-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #eef2f6;
}

.quick-links a,
.quick-links span {
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f8fafc;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 0.01em;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.quick-links a {
  color: #2563eb !important;
  text-decoration: none;
}

.quick-links span {
  color: #64748b;
  cursor: default;
}

.quick-links a:hover {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8 !important;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.08);
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.feature-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 16px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: linear-gradient(180deg, #fafcfd 0%, #fff 100%);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.feature-card:hover {
  border-color: #cce8d8;
  box-shadow: 0 6px 16px rgba(11, 109, 71, 0.06);
}

.feature-card-icon {
  display: flex;
  flex: 0 0 44px;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 16px;
  line-height: 1;
}

.feature-card-icon--green {
  background: #e8f7ef;
  color: #0b6d47;
}

.feature-card-icon--blue {
  background: #e8f2fc;
  color: #2563eb;
}

.feature-card-icon--orange {
  background: #fff3e8;
  color: #ea580c;
}

.feature-card-icon--teal {
  background: #e6f7f5;
  color: #0d9488;
}

.feature-card-icon--purple {
  background: #f3ecfb;
  color: #9333ea;
}

.feature-card-icon--indigo {
  background: #eef0ff;
  color: #4f46e5;
}

.feature-card-body {
  min-width: 0;
}

.feature-card-body h3 {
  margin: 2px 0 5px;
  color: #14213d;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
}

.feature-card-body p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.55;
}

.text-block + .text-block {
  margin-top: 28px;
  padding-top: 28px;
  border-top: 1px solid #eef2f6;
}

.text-image {
  display: block;
  width: auto;
  max-width: min(360px, 100%);
  height: auto;
  margin: 20px auto 0;
  padding: 8px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: #f8fafc;
  object-fit: contain;
  image-rendering: auto;
}

.table-wrap {
  overflow-x: auto;
  border: 1px solid #e8edf3;
  border-radius: 12px;
}

.info-table,
.comparison-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}

.info-table th,
.info-table td,
.comparison-table th,
.comparison-table td {
  padding: 13px 16px;
  border-bottom: 1px solid #e8edf3;
  text-align: left;
  vertical-align: top;
}

.info-table tr:last-child th,
.info-table tr:last-child td,
.comparison-table tbody tr:last-child th,
.comparison-table tbody tr:last-child td {
  border-bottom: none;
}

.info-table th,
.comparison-table th {
  width: 30%;
  background: #f8fafc;
  color: #14213d;
  font-weight: 700;
}

.comparison-table thead th {
  background: linear-gradient(180deg, #f0faf4 0%, #e8f7ef 100%);
  color: #0b6d47;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.comparison-table tbody tr:nth-child(even) td {
  background: #fafcfd;
}

.check-list,
.risk-list,
.step-list {
  margin: 8px 0 18px 22px;
  padding-left: 0;
  color: #3c4b62;
  line-height: 1.7;
}

.check-list {
  list-style: none;
  margin-left: 0 !important;
  padding-left: 0 !important;
}

.check-list li {
  position: relative;
  margin-bottom: 10px;
  padding-left: 26px;
  list-style: none;
}

.check-list li::before {
  position: absolute;
  left: 0;
  top: 0.45em;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #e8f7ef url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath fill='%230b6d47' d='M4.5 8.2 2.3 6l-.8.8L4.5 9.8l6-6-.8-.8z'/%3E%3C/svg%3E") center / 10px no-repeat;
  content: '';
}

.risk-list li::marker {
  color: #b42318;
}

.step-list li {
  margin-bottom: 12px;
}

.step-list span {
  display: block;
  margin-top: 2px;
}

.step-timeline {
  list-style: none;
  margin: 20px 0 0;
  padding: 0;
}

.step-timeline-item {
  position: relative;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  padding-bottom: 24px;
}

.step-timeline-item:last-child {
  padding-bottom: 0;
}

.step-timeline-item:not(:last-child)::after {
  position: absolute;
  top: 40px;
  bottom: 0;
  left: 20px;
  width: 2px;
  background: linear-gradient(180deg, #b8e6cf 0%, #e5f5ec 100%);
  content: '';
  transform: translateX(-50%);
}

.step-timeline-marker {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  justify-self: center;
  box-sizing: border-box;
  width: 40px;
  height: 40px;
  border: 2px solid #9dd4b8;
  border-radius: 50%;
  background: #f4fbf7;
  color: #0b6d47;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.step-timeline-body {
  padding-top: 8px;
}

.step-timeline-title {
  display: block;
  color: #14213d;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
}

.step-timeline-text {
  margin: 6px 0 0;
  color: #3c4b62;
  font-size: 15px;
  line-height: 1.65;
}

.step-timeline-text--solo {
  margin: 0;
  padding-top: 2px;
  font-size: 16px;
  line-height: 1.7;
}

.guide-dot-list {
  list-style: none !important;
  margin: 12px 0 0 !important;
  padding: 0 !important;
}

.guide-dot-list li {
  position: relative;
  margin-bottom: 10px;
  padding-left: 22px;
  list-style: none !important;
  color: #3c4b62;
  line-height: 1.7;
}

.guide-dot-list li::before {
  position: absolute;
  top: 0.85em;
  left: 2px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2563eb;
  content: '';
  transform: translateY(-50%);
}

.guide-dot-list li::marker {
  content: '';
}

.guide-dot-list::v-deep li strong {
  color: inherit;
  font-weight: inherit !important;
  white-space: nowrap;
}

.guide-dot-list li:last-child {
  margin-bottom: 0;
}

.two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
}

.pros-cons-section {
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
}

.pros-cons-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.pros-cons-panel {
  padding: 24px 22px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.pros-cons-panel--pro {
  background: linear-gradient(180deg, #f6fbf8 0%, #fff 100%);
  border-color: #cce8d8;
}

.pros-cons-panel--con {
  background: linear-gradient(180deg, #fff7f7 0%, #fff 100%);
  border-color: #f5d5d5;
}

.pros-cons-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 16px;
  color: #14213d;
  font-size: 22px;
  line-height: 1.2;
}

.pros-cons-heading-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 13px;
  line-height: 1;
}

.pros-cons-heading-icon--pro {
  background: #e8f7ef;
  color: #0b6d47;
}

.pros-cons-heading-icon--con {
  background: #fdecec;
  color: #b42318;
}

.pros-cons-list {
  list-style: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

.pros-cons-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.pros-cons-item:last-child {
  margin-bottom: 0;
}

.pros-cons-icon {
  display: flex;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 14px;
  line-height: 1;
}

.pros-cons-icon--pro {
  background: #e8f7ef;
  color: #0b6d47;
}

.pros-cons-icon--con {
  background: #fdecec;
  color: #b42318;
}

.pros-cons-item p {
  margin: 0;
  color: #3c4b62;
  font-size: 15px;
  line-height: 1.6;
}

.faq-accordion {
  display: grid;
  gap: 10px;
  margin-top: 18px;
}

.faq-item {
  overflow: hidden;
  border: 1px solid #dbe8e1;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(16, 54, 40, 0.04);
}

.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 16px 18px;
  border: 0;
  background: transparent;
  color: #15372a;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.4;
  text-align: left;
  cursor: pointer;
}

.faq-question-text {
  flex: 1;
}

.faq-icon {
  position: relative;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
}

.faq-icon::before,
.faq-icon::after {
  position: absolute;
  top: 50%;
  left: 50%;
  background: #15372a;
  content: '';
  transform: translate(-50%, -50%);
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.faq-icon::before {
  width: 12px;
  height: 2px;
}

.faq-icon::after {
  width: 2px;
  height: 12px;
}

.faq-item.is-open .faq-icon::after {
  opacity: 0;
  transform: translate(-50%, -50%) scaleY(0);
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.28s ease;
}

.faq-item.is-open .faq-answer {
  max-height: 500px;
}

.faq-answer p {
  margin: 0;
  padding: 0 18px 16px;
  color: #4d5f58;
  font-size: 14px;
  line-height: 1.6;
}

@media (max-width: 900px) {
  .home-hero {
    grid-template-columns: 1fr;
    grid-template-areas:
      "visual"
      "copy";
    padding: 28px 20px;
  }

  .hero-visual {
    justify-content: center;
  }

  .hero-phone-image {
    width: min(248px, calc(72vw - 18px));
  }

  .feature-grid,
  .two-column,
  .pros-cons-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .home-main {
    width: min(100% - 20px, 1120px);
    padding-top: 12px;
    padding-bottom: 48px;
  }

  .home-hero,
  .content-section {
    padding: 24px 18px;
  }

  .content-section h2 {
    font-size: 22px;
  }

  .home-hero {
    border-radius: 16px;
  }

  .hero-copy h1 {
    font-size: 31px;
    letter-spacing: -0.01em;
  }

  .primary-download {
    width: 100%;
  }

  .quick-links {
    gap: 8px;
  }

  .quick-links a,
  .quick-links span {
    padding: 6px 12px;
    font-size: 12px;
  }

  .faq-question {
    padding: 14px 16px;
    font-size: 15px;
  }

  .faq-answer p {
    padding: 0 16px 14px;
    font-size: 13px;
  }
}
</style>
